// Pacific Key Management — full bilingual UI strings.
// Access via useLanguage().t("section.key").

export const translations = {
    en: {
        nav: {
            home: "Home",
            about: "About",
            services: "Services",
            rentals: "Pacific Rentals",
            maintConstruct: "Maintenance & Construction",
            concierge: "Concierge",
            blog: "Journal",
            testimonials: "Client Testimonials",
            contact: "Contact",
            consultation: "Request Consultation",
        },
        common: {
            requestConsultation: "Request Consultation",
            exploreServices: "Explore Services",
            learnMore: "Learn More",
            discover: "Discover",
            scrollDown: "Scroll",
            viewAll: "View All",
            inquire: "Inquire",
            sendInquiry: "Send Inquiry",
            sending: "Sending",
        },
        form: {
            name: "Full Name",
            email: "Email",
            phone: "Phone",
            propertyLocation: "Community / Property Location",
            propertyLocationOptional: "Optional",
            serviceInterest: "Service Interest",
            message: "Message",
            namePh: "Your full name",
            emailPh: "you@example.com",
            phonePh: "+1 555 000 0000",
            messagePh: "Share a few details about your property and what you're looking for.",
            selectCommunity: "Select a community",
            selectService: "Select a service",
            notSure: "Not Sure Yet / Currently Evaluating",
            other: "Other / Not Listed",
            successTitle: "Thank you",
            successEyebrow: "Received",
            successBody: "A member of our private estate office will be in touch within one business day to schedule your consultation.",
            sendAnother: "Send another inquiry",
            successToast: "Your consultation request has been received.",
            failToast: "Unable to submit. Please try again.",
        },
        footer: {
            tagline: "Boutique luxury property and estate management for the most discerning homeowners in Los Cabos.",
            address: "Los Cabos · Baja California Sur · Mexico",
            quickLinks: "Quick Links",
            services: "Services",
            contact: "Contact",
            rights: "All rights reserved.",
            bilingual: "Bilingual service · English & Español",
            followUs: "Follow Pacific Key Management",
        },
        home: {
            heroEyebrow: "Pacific Key Management",
            heroTitle1: "Securing Your Investment,",
            heroTitle2: "Enhancing Your Stay.",
            heroSubtitle: "Boutique luxury property and estate management for high-end residences in Los Cabos.",
            ctaConsult: "Request Consultation",
            ctaServices: "Explore Services",
            trustEyebrow: "A Private Estate Office",
            trustTitle: "Operational excellence, quietly delivered.",
            aboutEyebrow: "A Different Philosophy",
            aboutTitle: "A boutique private estate office for Los Cabos.",
            aboutBody1: "We exist for a select group of homeowners who expect their property to operate at the standards of a luxury hotel — quietly, anticipatorily, and without compromise. Our team blends preventative care, structured reporting, and hospitality discipline into a single, trusted relationship.",
            aboutBody2: "From the first inspection to your family's arrival, every detail is handled by people who understand luxury operations and the responsibility of safeguarding a meaningful investment.",
            aboutLink: "Our Philosophy",
            servicesEyebrow: "Services",
            servicesTitle: "Quietly comprehensive. Visibly considered.",
            improvementsEyebrow: "Property Improvements & Project Oversight",
            improvementsTitle: "Protecting and enhancing your investment.",
            improvementsSubtitle: "Proactive maintenance, renovation oversight, and trusted construction coordination — managed locally, on your behalf.",
            communitiesEyebrow: "Communities",
            communitiesTitle: "We serve Los Cabos' most discerning addresses.",
            communitiesCta: "View All Communities",
            lifestyleEyebrow: "The Cabo Lifestyle",
            lifestyleTitle: "Curated for the way you actually live here.",
            whyEyebrow: "Why PKM",
            whyTitle: "The standard of a private estate office.",
            testimonialsEyebrow: "In Their Words",
            testimonialsTitle: "Trusted by homeowners across Los Cabos.",
            trustedEyebrow: "In Their Words",
            trustedTitle: "Trusted by Homeowners",
            trustedBody: "Relationships built on trust, communication and exceptional care.",
            trustedCta: "Read Client Testimonials",
            ctaEyebrow: "Begin",
            ctaTitle: "Request a private consultation.",
            ctaBody: "Share a few details about your residence. A member of our team will follow up within one business day to arrange a private conversation — at your villa, by phone, or video.",
            ctaDirect: "Direct",
            ctaEmail: "Email",
        },
        trust: {
            specialists: "Luxury Community Specialists",
            experience: "10+ Years of Local Experience",
            support: "24 / 7 Emergency Support",
            preventative: "Preventative Property Oversight",
            bilingualLabel: "Bilingual Communication",
            transparent: "Transparent Vendor Coordination",
            // Subtle hero trust bar
            since1996: "Los Cabos Resident Since 1996",
            since2015: "Luxury Property Management Since 2015",
            bilingual: "Bilingual English & Spanish",
            emergency: "24/7 Emergency Support",
            boutique: "Boutique Portfolio Approach",
        },
        improvements: {
            preventative: { title: "Preventative Maintenance", body: "Scheduled care programs designed to identify small issues before they become costly problems." },
            renovation: { title: "Renovation Oversight", body: "From kitchen refreshes to full interior updates, PKM supervises vendors, timelines, and quality on behalf of the owner." },
            construction: { title: "Construction Coordination", body: "Local representation for owners during construction projects — site visits, progress reporting, and contractor communication." },
            vendors: { title: "Vendor Supervision", body: "Trusted professionals, coordinated and supervised with clear communication, documentation, and accountability." },
        },
        whyPkm: {
            proactive: { title: "Proactive Oversight", body: "Issues identified and resolved before they reach you. Scheduled, structured, and quietly thorough." },
            boutique: { title: "Boutique Attention", body: "A select portfolio of residences — never volume-driven. Each home receives the depth of attention it deserves." },
            vendors: { title: "Trusted Vendor Network", body: "Curated, vetted local partners across maintenance, hospitality, and specialty trades — supervised on your behalf." },
            reporting: { title: "Structured Reporting", body: "Photo-rich monthly reports, transparent financials, and clear communication in English or Spanish." },
            response: { title: "Fast Response Times", body: "An on-call team for emergencies, vendor escalations, and last-minute arrival needs — anytime." },
            standards: { title: "Luxury Hospitality Standards", body: "Trained in the operating culture of the world's leading resorts — applied privately to your home." },
        },
        testimonials: {
            t1: { quote: "What we appreciate most is the communication. We always know what's happening at the house, and issues are handled before they become problems.", author: "Private Homeowner", location: "Villas del Mar" },
            t2: { quote: "Living outside Mexico, we needed someone we could trust locally. Fernando and his team have given us that peace of mind.", author: "Seasonal Homeowner", location: "Palmilla" },
            t3: { quote: "From vendor coordination to hurricane preparation, PKM keeps everything organized and clear. That level of oversight is exactly what we needed.", author: "Private Client", location: "Los Cabos" },
        },
        about: {
            heroEyebrow: "About",
            heroTitle1: "A private estate office —",
            heroTitle2: "redefined for Cabo.",
            heroSubtitle: "Pacific Key Management was founded on a simple premise: the most discerning homeowners in Los Cabos deserve the operational standards of the world's leading resorts — applied privately to their residences.",
            storyEyebrow: "Our Story",
            storyTitle: "Built quietly. Operated meticulously.",
            storyP1: "Pacific Key began with a small, hand-selected portfolio of residences in Palmilla and Villas del Mar — homes whose owners required a level of attention that the conventional property-management model simply could not deliver.",
            storyP2: "Over a decade later, we remain deliberately boutique. We accept residences only when we can give them the depth of care they require — never volume-driven, always relationship-led.",
            storyP3: "Today, our team operates across Los Cabos' most prestigious gated communities, blending hospitality discipline, preventative engineering, and quiet hospitality to safeguard the homes — and the lives — entrusted to us.",
            founderEyebrow: "The Founder's Promise",
            founderTitle: "\"Your residence will be cared for as if it were our own.\"",
            founderP1: "Pacific Key was founded by hospitality professionals with deep operational backgrounds inside Los Cabos' leading resorts and private clubs. We bring that culture — refined service, structured operations, anticipatory care — into the private homes we serve.",
            founderP2: "Every owner relationship begins with a private conversation, an honest assessment of what your residence needs, and a clear operating plan tailored to it.",
            // Meet the Team
            teamEyebrow: "Meet the Team",
            teamTitle: "The people behind Pacific Key Management.",
            teamLead: "A boutique estate management company led by people who are personally invested in protecting your home.",
            team1Name: "Luis Fernando Mendoza",
            team1Title: "Founder & Managing Director",
            team1Initials: "LM",
            team1P1: "Luis Fernando Mendoza has called Los Cabos home since 1996 and has specialized in luxury property and estate management since 2015.",
            team1P2: "With a background rooted in hospitality, customer service, and private estate operations, Luis brings a hands-on approach to every property under Pacific Key Management's care.",
            team1P3: "Having worked within prestigious communities including Palmilla, Villas del Mar, and Querencia, he understands the unique expectations of luxury homeowners and believes that preventative maintenance, proactive communication, and attention to detail are the foundation of exceptional property management.",
            team1P4: "As a long-term resident, family man, and trusted local resource, Luis remains personally involved in inspections, owner communication, project oversight, and concierge coordination.",
            team2Name: "Fernando Mendoza Rocha",
            team2Title: "Operations Coordinator & Bookkeeper",
            team2Initials: "FM",
            team2P1: "Fernando Mendoza Rocha supports the operational and administrative backbone of Pacific Key Management.",
            team2P2: "His responsibilities include financial tracking, reporting, vendor coordination, scheduling, bookkeeping, and ensuring that day-to-day operations run efficiently and transparently.",
            team2P3: "Working closely with homeowners, vendors, and service providers, Fernando helps maintain the structured systems and communication standards that Pacific Key Management is known for.",
            team3Name: "Trusted Vendor Network",
            team3Title: "Maintenance, Construction & Concierge Partners",
            team3P1: "Pacific Key Management works alongside a carefully selected network of licensed professionals, contractors, housekeeping specialists, concierge providers, landscapers, pool technicians, and hospitality partners throughout Los Cabos.",
            team3P2: "Every vendor is selected based on reliability, professionalism, responsiveness, and proven performance within luxury residential environments.",
            stat1Number: "1996",
            stat1Label: "Los Cabos Local Experience",
            stat2Number: "2015",
            stat2Label: "Luxury Property Management",
            stat3Number: "Bilingual",
            stat3Label: "English & Spanish Communication",
            stat4Number: "Boutique",
            stat4Label: "Relationship-Driven Portfolio",
            valuesEyebrow: "Our Values",
            valuesTitle: "Quiet standards we never compromise.",
            v1: { title: "Discretion", body: "We move quietly through your home. Privacy is the foundation of every protocol we operate." },
            v2: { title: "Excellence", body: "Trained in luxury hospitality, our standards exceed the conventions of property management." },
            v3: { title: "Trust", body: "We earn the right to safeguard your investment through transparency, consistency, and care." },
            v4: { title: "Local Mastery", body: "Decades of combined experience inside Los Cabos' most prestigious private communities." },
            standardsEyebrow: "Operational Standards",
            standardsTitle: "The rhythm behind a perfectly run residence.",
            standardsBody: "Behind every Pacific Key home is a structured operating cadence — designed to detect issues before they arise, and to deliver the consistency a luxury residence demands.",
            s1: { label: "Inspections", body: "Routine, structured, photo-documented." },
            s2: { label: "Reporting", body: "Monthly, transparent, bilingual." },
            s3: { label: "Vendors", body: "Vetted, insured, supervised on-site." },
            s4: { label: "Response", body: "On-call 24 / 7 for emergencies and arrivals." },
            outroTitle: "Considering Pacific Key?",
            outroBody: "We accept a limited number of residences each year. Reach out for a private conversation.",
        },
        services: {
            heroEyebrow: "Services",
            heroTitle1: "Comprehensive care,",
            heroTitle2: "quietly executed.",
            heroSubtitle: "Every service is designed with the standards of a private estate office. Choose individual offerings or our full stewardship program.",
            sectionEyebrow: "Service",
            processEyebrow: "Process",
            processTitle: "From inquiry to ongoing stewardship.",
            p1: { title: "Private Consultation", body: "A confidential conversation about your residence, expectations, and current pain points." },
            p2: { title: "Property Audit", body: "On-site walkthrough, condition assessment, and tailored operating plan." },
            p3: { title: "Operating Cadence", body: "Scheduled inspections, vendor calendar, reporting structure, and concierge baseline." },
            p4: { title: "Ongoing Stewardship", body: "Transparent monthly reports, fast response, anticipatory care — month after month." },
            outroEyebrow: "Tailored to Your Residence",
            outroTitle: "Build your operating plan.",
            outroBody: "Every plan is bespoke. Tell us about your residence and we'll structure an appropriate scope.",
        },
        rentals: {
            // Hero
            heroEyebrow: "Pacific Rentals",
            heroTitle1: "Exceptional Homes.",
            heroTitle2: "Personally Managed.",
            heroSubtitle: "A curated collection of exceptional private residences in Los Cabos, professionally managed and locally supported by Pacific Key Management.",
            heroCtaFeatured: "Explore Hacienda 511",
            heroCtaPlan: "Plan Your Stay",

            // Why Book Direct
            directEyebrow: "Why Book Direct",
            directTitle: "The advantages of booking directly.",
            directSubtitle: "Reserving your stay directly with Pacific Key Management means a more personal experience from first inquiry to departure.",
            direct_rate: "Best available direct rate",
            direct_planning: "Personalized pre-arrival planning",
            direct_support: "Local support throughout your stay",
            direct_concierge: "Direct access to our concierge team",
            direct_nofees: "No marketplace booking fees",

            // Section 2 — Featured Residence
            featuredEyebrow: "Featured Residence",
            featuredTitle: "Introducing Hacienda 511.",
            featuredSubtitle: "Our flagship luxury villa experience in Villas del Mar.",
            featuredViewCta: "View Residence",
            featuredFlagship: "Flagship Residence",

            // Section 3 — The Pacific Rentals Experience
            experienceEyebrow: "The Pacific Rentals Experience",
            experienceTitle: "Effortless from arrival to departure.",
            experienceBody: "We believe luxury travel is about more than the residence itself. Every stay should feel effortless, personalized, and memorable from arrival to departure.",
            exp_chef: "Private Chef Services",
            exp_butler: "Butler Services",
            exp_bartender: "Bartender & Mixologist Services",
            exp_grocery: "Grocery Pre-Stocking",
            exp_transport: "Private Transportation",
            exp_golf: "Golf Concierge",
            exp_yacht: "Yacht Charters",
            exp_spa: "Spa & Wellness Services",

            // Section 4 — Why Guests Choose Pacific Rentals
            whyEyebrow: "Why Guests Choose Pacific Rentals",
            whyTitle: "Quietly different from every booking platform.",
            why_curated: { title: "Curated Collection", body: "Every residence is selected based on location, quality, maintenance standards, and guest experience." },
            why_concierge: { title: "Personalized Concierge", body: "Tailored experiences before and during your stay." },
            why_standards: { title: "Luxury Hospitality Standards", body: "Attention to detail inspired by the world's leading resorts." },
            why_local: { title: "Local Expertise", body: "A team based in Los Cabos with extensive local knowledge and trusted relationships." },

            // Section 5 — Our Collection Standards
            standardsEyebrow: "Our Collection Standards",
            standardsTitle: "Not every villa qualifies for Pacific Rentals.",
            standardsBody: "We carefully evaluate each residence based on:",
            c_location: "Location",
            c_design: "Design & Architecture",
            c_privacy: "Privacy",
            c_maintenance: "Maintenance Standards",
            c_owner: "Owner Commitment",
            c_guest: "Guest Experience",
            c_concierge: "Concierge Potential",
            standardsClosing: "Some residences are directly managed by Pacific Key Management, while others are represented through carefully selected partnerships that align with our hospitality philosophy.",

            // Section 6 — Coming Soon
            comingEyebrow: "Coming Soon",
            comingTitle: "An evolving collection.",
            comingSubtitle: "Additional luxury residences will be added to our collection as we continue to expand our portfolio throughout Los Cabos' most prestigious communities.",
            comingLabel: "Coming Soon",

            // Section 7 — For Homeowners (kept)
            ownerEyebrow: "For Homeowners",
            ownerTitle: "Generate income without sacrificing peace of mind.",
            ownerBody: "For owners who wish to rent their residence on their own terms — without compromising privacy, condition, or care. Our program is the antithesis of high-volume booking platforms.",
            ownerCta: "Enroll Your Residence",
            ob1: { title: "Owner-First Program", body: "Your home, your dates. We work around your usage and protect the asset between stays." },
            ob2: { title: "Vetted Guests Only", body: "Discreet inquiry screening, identity verification, and curated booking calendars." },
            ob3: { title: "Hospitality Operations", body: "Resort-trained staffing, immaculate turnover, and pre-stocked arrivals." },
            ob4: { title: "Transparent Reporting", body: "Owner statements, performance metrics, and quarterly portfolio reviews." },

            // Final CTA
            outroEyebrow: "Plan Your Los Cabos Escape",
            outroTitle: "Tell us about your stay.",
            outroBody: "Tell us about your travel dates and preferences, and our team will help curate the perfect villa experience.",
            outroCtaAvailability: "Request Availability",
            outroCtaConcierge: "Contact Concierge",

            // Shared / Villa detail
            collectionEyebrow: "The Collection",
            collectionTitle: "The Pacific Rentals Collection.",
            curated: "Curated",
            bedroomsShort: "Bedrooms",
            bathsShort: "Baths",
            powderShort: "Powder Rooms",
            sleepsShort: "Sleeps",
            viewVilla: "View Residence",
            villaBack: "Back to Collection",
            villaHighlights: "Highlights",
            villaInquire: "Inquire About This Villa",
            villaNotFound: "Villa not found",
            villaAmenities: "Amenities",
            villaPlaceholder: "Photography for illustration · final villa portfolio expanding",
        },
        villa: {
            // Section labels & shared CTAs
            requestAvailability: "Check Availability",
            checkAvailability: "Check Availability",
            requestToBook: "Request to Book",
            whatsappConcierge: "WhatsApp Concierge",
            downloadBrochure: "Download Villa Brochure",
            speakConcierge: "WhatsApp Concierge",

            // Section 2 — Extraordinary Retreat
            retreatEyebrow: "An Extraordinary Los Cabos Retreat",
            retreatTitle: "An Extraordinary Los Cabos Retreat.",
            retreatP1: "Nestled within the prestigious Villas del Mar community, Hacienda 511 combines timeless Mexican architecture, expansive indoor-outdoor living, and exceptional amenities to create an unforgettable private retreat.",
            retreatP2: "Designed for family gatherings, celebrations, and luxury escapes, every space encourages relaxation, connection, and memorable experiences in Los Cabos.",

            // Section 3 — Why Guests Love
            whyEyebrow: "Why Guests Love Hacienda 511",
            whyTitle: "Designed for memorable moments.",
            f_theater: { title: "Private Theater", body: "Family movie nights in a dedicated cinema experience." },
            f_pool: { title: "Pool & Jacuzzi", body: "Designed for long days under the Baja sun." },
            f_firepit: { title: "Firepit Lounge", body: "Gather for sunset cocktails and evening conversations." },
            f_outdoor: { title: "Outdoor Living", body: "Multiple dining and lounge spaces for entertaining." },
            f_golfCart: { title: "Golf Cart Included", body: "Convenient transportation throughout the community." },
            f_concierge: { title: "Private Concierge Services", body: "Personalized planning before and during your stay." },

            // Section 4 — Lifestyle Gallery
            galleryEyebrow: "Lifestyle Gallery",
            galleryTitle: "Life at Hacienda 511.",
            galleryBody: "An immersive look at the spaces, light, and rhythm of a stay.",
            viewAllPhotos: "View All Photos",

            // Also known as — recognized market/search alias (same residence)
            alsoKnownAs: "Hacienda 511 is also known as Villa Bravura del Mar — a seven-bedroom private residence in Villas del Mar, Los Cabos.",

            // Included With Your Stay
            includedEyebrow: "Included With Your Stay",
            includedTitle: "Part of the residence experience.",
            includedBody: "Every stay at Hacienda 511 includes attentive daily care and easy access throughout the community. Guests who reserve directly with Pacific Key Management also enjoy dedicated butler service and a private chef for breakfast and lunch.",
            inc_housekeeping: { title: "Daily Housekeeping", body: "Attentive daily housekeeping throughout your stay." },
            inc_golfCart: { title: "One Golf Cart", body: "A dedicated golf cart for easy access throughout Villas del Mar." },
            inc_butler: { title: "Butler Service", body: "In-villa butler service looking after your day-to-day needs." },
            inc_chef: { title: "Private Chef · Breakfast & Lunch", body: "A private chef prepares breakfast and lunch in the villa each day." },
            badgeIncluded: "Included",
            badgeDirect: "Direct PKM Reservations",
            includedNote: "Daily housekeeping and one golf cart are included with every stay. Butler service and the breakfast & lunch private chef are included for reservations made directly with Pacific Key Management.",

            // Personalized Experiences (optional — arranged upon request)
            personalizedEyebrow: "Personalized Experiences",
            personalizedTitle: "Thoughtfully arranged around your stay.",
            personalizedBody: "Beyond what's included, our concierge team can arrange a range of optional experiences tailored to your party — from private dinners to time on the water.",
            px_dinnerChef: "Private Dinner Chef",
            px_bartender: "Bartender & Mixologist",
            px_grocery: "Grocery Pre-Stocking",
            px_transport: "Private Transportation & Transfers",
            px_golf: "Golf Concierge",
            px_yacht: "Yacht Charters",
            px_spa: "Spa & Wellness",
            px_dining: "Restaurant Reservations",
            personalizedNote: "Optional experiences are arranged upon request and are not included in the nightly rate.",

            // Section 5 — Elevate Your Stay
            elevateEyebrow: "Elevate Your Stay",
            elevateTitle: "Personalized experiences, quietly arranged.",
            elevateBody: "Our concierge team can arrange personalized experiences designed around your preferences.",
            e_chef: "Private Chef Services",
            e_butler: "Butler Services",
            e_bartender: "Bartender & Mixologist Services",
            e_grocery: "Grocery Pre-Stocking",
            e_transport: "Private Transportation",
            e_golf: "Golf Concierge",
            e_yacht: "Yacht Charters",
            e_spa: "Spa & Wellness Services",

            // Section 6 — Your Stay, Your Way (curated personalized experiences)
            itineraryEyebrow: "Curated Experiences",
            itineraryTitle: "Your Stay, Your Way",
            itineraryBody: "Every stay at Hacienda 511 is shaped around you — never pre-packaged. Consider these curated experiences, arranged by our concierge team around your party's pace and preferences.",
            exp_chef: { title: "Private Chef Dining", body: "In-villa menus crafted by a private chef, from casual poolside lunches to elegant multi-course dinners." },
            exp_pool: { title: "Pool & Villa Days", body: "Unhurried days between the infinity pool, jacuzzi and shaded terraces, with full villa service." },
            exp_spa: { title: "Spa & Wellness", body: "In-residence massages, wellness rituals and personal training arranged at your convenience." },
            exp_yacht: { title: "Yacht Charters", body: "A private day on the Sea of Cortez with curated provisioning and crew." },
            exp_golf: { title: "Golf", body: "Tee times at the region's celebrated championship courses, coordinated for your group." },
            exp_beach: { title: "Beach & Coastal", body: "Swimmable beaches, coastal escapes and sunset moments along the Los Cabos corridor." },
            exp_dining: { title: "Dining in Los Cabos", body: "Reservations and transportation to the finest restaurants in San José and Cabo San Lucas." },
            exp_concierge: { title: "Personalized Concierge", body: "Celebrations, transfers and bespoke requests — thoughtfully handled from first inquiry to departure." },
            it_day4: { day: "Day Four", title: "Spa & Wellness", body: "In-villa massages, yoga at sunrise, and a private chef tasting." },
            it_day5: { day: "Day Five", title: "Family Beach Day", body: "Beach club access, water sports, and an outdoor barbecue at the villa." },
            it_day6: { day: "Day Six", title: "Adventure or Custom", body: "Sport fishing, ATV in the desert, or a private cultural tour." },
            it_departure: { day: "Day Seven", title: "Departure", body: "A leisurely farewell breakfast and seamless airport transfer." },

            // Section 7 — Accommodations
            accomEyebrow: "Accommodations",
            accomTitle: "Seven bedrooms. Built for fourteen guests.",
            accomBody: "Seven bedrooms, seven full bathrooms and two powder rooms — sleeping up to fourteen guests.",
            b1: { label: "Master Suite", body: "One king bed." },
            b2: { label: "Second Master", body: "One king bed." },
            b3: { label: "Guest Suite I", body: "One king bed." },
            b4: { label: "Guest Suite II", body: "One king bed." },
            b5: { label: "Family Suite", body: "Two queen beds." },
            b6: { label: "Children's Suite", body: "One double bed and one twin bed." },
            b7: { label: "Casita", body: "Two queen beds." },
            countBedrooms: "Bedrooms",
            countBaths: "Bathrooms",
            countSleeps: "Guest Capacity",

            // Section 8 — Seasonal Rates
            ratesEyebrow: "Rates",
            ratesTitle: "Seasonal Rates",
            ratesNote: "Rates are quoted in USD per night and are subject to availability, plus applicable taxes and fees.",
            minStayNote: "Minimum stay: 3 nights standard · 5 nights over Thanksgiving · 7 nights over Christmas & New Year.",
            r_low: { label: "Low Season", period: "May – October" },
            r_high: { label: "High Season", period: "November – April" },
            r_holiday: { label: "Holiday Season", period: "Thanksgiving · Easter Week" },
            r_festive: { label: "Christmas / New Year", period: "December 20 – January 5" },
            rateSuffix: "USD / Night",

            // Section 9 — Pacific Rentals Difference
            differenceEyebrow: "The Pacific Rentals Difference",
            differenceTitle: "Exceptional homes. Unforgettable stays.",
            differenceP1: "Every residence within our collection is selected for its quality, location, maintenance standards, and guest experience.",
            differenceP2: "Whether managed directly by Pacific Key Management or represented through trusted partnerships, our goal remains the same: exceptional homes and unforgettable stays in Los Cabos.",

            // Section 10 — Availability Inquiry
            inquireEyebrow: "Availability Inquiry",
            inquireTitle: "Begin planning your stay.",
            inquireBody: "Share your preferred dates and party size, and a member of our concierge team will respond within one business day.",
            f_dates: "Preferred Dates",
            f_datesPh: "e.g. Dec 22 – 29",
            f_guests: "Guest Count",
            f_guestsPh: "How many guests?",
            f_special: "Special Requests",
            f_specialPh: "Anniversaries, accessibility, dietary preferences…",
            f_message: "Message",
            f_messagePh: "Tell us about your stay.",
            contactPhone: "Phone",
            contactWhatsapp: "WhatsApp",
            contactEmail: "Email",
        },
        communities: {
            heroEyebrow: "Communities",
            heroTitle1: "The most discerning addresses",
            heroTitle2: "in Los Cabos.",
            heroSubtitle: "From historic Palmilla to the modern oceanfront of Chileno Bay, we operate with depth inside the communities that define luxury living in Baja.",
            propertyTypes: "Property Types",
            pkmApproach: "PKM Support Approach",
            pkmApproachBody: "Dedicated community specialist · on-site routine inspections · vetted local vendor network · community-specific HOA coordination · privacy-first protocols.",
            outroTitle: "Owners in another community?",
            outroBody: "Our portfolio extends beyond these flagship enclaves. Reach out — we may already serve your address.",
            outroCta: "Inquire About Your Community",
        },
        concierge: {
            // Hero
            heroEyebrow: "Concierge",
            heroTitle1: "Personalized Concierge",
            heroTitle2: "Services.",
            heroSubtitle: "From private chefs and yacht charters to golf reservations and family experiences, Pacific Key Management delivers tailored services designed around your lifestyle.",
            heroCta: "Plan Your Stay",

            // Section 1 — Curated Experiences
            introEyebrow: "A Philosophy of Service",
            introTitle: "Curated experiences, tailored to you.",
            introBody: "Every homeowner and guest has different priorities. Whether you're seeking relaxation, adventure, family activities, wellness experiences, or exclusive access, our concierge team coordinates every detail before and during your stay.",
            introTagline: "We handle every detail of your stay — so you can focus on enjoying Los Cabos.",

            // Section 2 — Private Dining & Hospitality
            diningEyebrow: "Private Dining & Hospitality",
            diningTitle: "An in-villa hospitality experience.",
            d1: { title: "Private Chef Services", bullets: ["In-villa breakfast, lunch & dinner experiences", "Multi-course tasting menus", "Family-style dining", "Special celebrations"] },
            d2: { title: "Butler Services", bullets: ["Personalized daily assistance", "Beverage service", "Guest hospitality", "Villa support throughout the stay"] },
            d3: { title: "Bartender & Mixologist Services", bullets: ["Poolside cocktails", "Private events", "Wine pairings", "Signature experiences"] },
            d4: { title: "Grocery Pre-Stocking", bullets: ["Arrival-ready villas", "Custom shopping lists", "Dietary accommodations", "Premium wine and spirits requests"] },
            d5: { title: "Special Occasion Planning", bullets: ["Birthdays", "Anniversaries", "Family celebrations", "Corporate retreats"] },

            // Section 3 — Ocean & Yacht
            oceanEyebrow: "Ocean & Yacht Experiences",
            oceanTitle: "The Sea of Cortez, on your terms.",
            oceanBody: "From sunrise charters to sunset cruises, our marine experiences are coordinated with vetted captains and crews — provisioned to your taste and tailored to your party.",
            o1: { title: "Private Yacht Charters", bullets: ["Sunset cruises", "Full-day luxury charters", "Multi-family outings"] },
            o2: { title: "Sport Fishing", bullets: ["Marlin", "Tuna", "Dorado", "Seasonal excursions"] },
            o3: { title: "Luxury Catamarans", bullets: ["Family-friendly experiences", "Snorkeling adventures", "Coastal exploration"] },
            o4: { title: "Whale Watching", bullets: ["Seasonal private tours"] },
            o5: { title: "Snorkeling & Diving", bullets: ["Cabo's premier marine locations"] },
            o6: { title: "Water Sports", bullets: ["Paddleboarding", "Kayaking", "Jet ski experiences"] },

            // Section 4 — Golf
            golfEyebrow: "Golf Experiences",
            golfTitle: "Some of the finest golf in North America.",
            golfBody: "Los Cabos is home to championship courses by Nicklaus, Norman, Tiger Woods, and Davis Love III — Diamante, El Dorado, Querencia, Quivira, Cabo del Sol and more. Our team coordinates every detail so your day is as effortless as your swing.",
            g1: "Tee time reservations",
            g2: "Private transportation",
            g3: "Golf club rentals",
            g4: "Tournament coordination",
            g5: "Multi-course itineraries",

            // Section 5 — Wellness
            wellnessEyebrow: "Wellness & Relaxation",
            wellnessTitle: "Rituals brought to your residence.",
            w1: { title: "In-Villa Spa Treatments", bullets: ["Massage therapy", "Facials", "Body treatments"] },
            w2: { title: "Private Fitness Sessions", bullets: ["Personal training", "Yoga", "Pilates"] },
            w3: { title: "Wellness Retreat Coordination", bullets: ["Multi-day programs", "Nutrition planning", "Holistic experiences"] },
            w4: { title: "Beauty Services", bullets: ["Hair styling", "Makeup artists", "Grooming services"] },

            // Section 6 — Family & Lifestyle
            familyEyebrow: "Family & Lifestyle Services",
            familyTitle: "Effortless logistics for the whole family.",
            f1: "Babysitting & Childcare",
            f2: "Family Activity Planning",
            f3: "Private Transportation",
            f4: "Airport Transfers",
            f5: "Luxury Vehicle Rentals",
            f6: "Golf Cart Coordination",
            f7: "Pet Assistance Services",
            f8: "Personal Shopping Assistance",

            // Section 7 — Exclusive Los Cabos
            exclusiveEyebrow: "Exclusive Los Cabos Experiences",
            exclusiveTitle: "Beyond the guidebook.",
            exclusiveBody: "Memorable, off-itinerary experiences arranged by hand — drawn from our deep network of artists, captains, sommeliers, photographers and local guides.",
            ex1: "San José Art Walk experiences",
            ex2: "Private cultural tours",
            ex3: "Culinary experiences",
            ex4: "Desert adventures",
            ex5: "ATV excursions",
            ex6: "Camel rides",
            ex7: "Luxury shopping assistance",
            ex8: "Photography sessions",
            ex9: "Private beach experiences",

            // Section 8 — Business & Executive
            businessEyebrow: "Business & Executive Support",
            businessTitle: "When business meets the Baja.",
            businessBody: "For homeowners and guests combining business with leisure travel, our team integrates seamlessly with your office — coordinating logistics with the same discretion we bring to your private life.",
            b1: "Executive transportation",
            b2: "Corporate retreat coordination",
            b3: "Meeting arrangements",
            b4: "Administrative assistance",
            b5: "Event planning support",

            // Section 9 — Why PKM Concierge
            whyEyebrow: "Why Guests Choose PKM Concierge",
            whyTitle: "The standard of a private lifestyle office.",
            why1: { title: "Local Expertise", body: "Decades of combined experience inside Los Cabos' most coveted communities and venues." },
            why2: { title: "Trusted Vendor Network", body: "A vetted bench of private chefs, captains, spa therapists and lifestyle partners." },
            why3: { title: "Personalized Recommendations", body: "Tailored to your party, your taste, and the rhythm of your stay." },
            why4: { title: "Reliable Communication", body: "A single point of contact, responsive across time zones — before, during, and after." },
            why5: { title: "Luxury Hospitality Standards", body: "Trained in the operating culture of the world's leading resorts and private clubs." },
            why6: { title: "Seamless Coordination", body: "Every supplier, transfer and reservation orchestrated quietly behind the scenes." },
            why7: { title: "Bilingual Support", body: "Native English and Spanish — communicating fluently in both worlds." },
            why8: { title: "Tailored Experiences", body: "Built around your interests, never packaged itineraries." },

            // Final CTA
            outroEyebrow: "Plan Your Stay",
            outroTitle: "Every stay, perfectly planned.",
            outroBody: "Whether you're visiting for a weekend, a season, or an extended stay, Pacific Key Management can coordinate every detail before you arrive.",
            outroCta: "Speak With Our Concierge Team",
        },
        contact: {
            heroEyebrow: "Contact",
            heroTitle1: "Begin a",
            heroTitle2: "private conversation.",
            heroSubtitle: "A member of our team will be in touch within one business day — by phone, email, or video, on your schedule.",
            directEyebrow: "Direct Contact",
            directTitle: "Our office, at your service.",
            telephone: "Telephone",
            emailGeneral: "General Inquiries",
            emailManagement: "Property Management",
            followUs: "Follow Pacific Key Management",
            office: "Office",
            languages: "Languages",
            languagesValue: "English · Español",
            serviceAreas: "Service Areas",
            formEyebrow: "Consultation Form",
            formTitle: "Tell us about your residence.",
        },
        maintConstruct: {
            heroEyebrow: "Maintenance & Construction",
            heroTitle1: "Maintenance, Renovation",
            heroTitle2: "& Construction Oversight.",
            heroSubtitle: "Protecting your investment through proactive maintenance, trusted vendor coordination, and professional project supervision.",
            ctaConsult: "Request Consultation",
            introEyebrow: "Owner Representation",
            introTitle: "Your eyes and ears on the ground.",
            introP1: "Many homeowners spend only part of the year in Los Cabos. Whether it's routine maintenance, a renovation, or a major construction project, Pacific Key Management provides local oversight to ensure every detail is handled professionally and transparently.",
            introP2: "We do not act as the contractor. We act as the owner's representative — coordinating vendors, verifying quality, controlling budgets, and reporting back to you with photo-rich documentation throughout.",
            maintEyebrow: "Maintenance",
            maintTitle: "Routine care, executed quietly.",
            m1: { title: "Preventative Maintenance Programs", body: "Scheduled inspections and proactive servicing — designed to catch issues before they escalate." },
            m2: { title: "Mechanical Equipment Monitoring", body: "Generators, pumps, water systems and home automation — checked and logged on a regular cadence." },
            m3: { title: "HVAC Coordination", body: "Routine cleaning, filter changes, refrigerant checks and vendor scheduling for full climate systems." },
            m4: { title: "Pool Equipment Monitoring", body: "Pump, heater, salt cell and chemistry oversight — coordinated with vetted pool specialists." },
            m5: { title: "Electrical & Plumbing Coordination", body: "Licensed local trades managed and supervised on site — never left to operate unsupervised." },
            m6: { title: "Seasonal Property Preparation", body: "Spring openings, summer closings, and shoulder-season transitions handled end-to-end." },
            m7: { title: "Hurricane Readiness", body: "Pre-season audits, shutter deployment, exterior securing, and post-storm inspections." },
            m8: { title: "Emergency Repairs", body: "A 24 / 7 on-call response for water, power, security, or weather events." },
            renoEyebrow: "Renovation",
            renoTitle: "Renovation project management.",
            renoBody: "We represent your interests from concept to final walkthrough — coordinating designers, contractors and craftsmen, and verifying quality so the finished work matches what was promised on paper.",
            r1: { title: "Interior Renovations", body: "Full or partial interior updates, supervised end-to-end." },
            r2: { title: "Kitchen Upgrades", body: "Cabinetry, appliances, surfaces — coordinated with luxury vendors." },
            r3: { title: "Bathroom Remodels", body: "Plumbing, stone, fixtures and finishes overseen for quality." },
            r4: { title: "Flooring Replacement", body: "Stone, hardwood, tile — sourced, installed, and inspected." },
            r5: { title: "Painting Programs", body: "Interior and exterior — vendor scope, color decks, and quality control." },
            r6: { title: "Outdoor Living Improvements", body: "Terraces, palapas, kitchens, and landscape upgrades." },
            r7: { title: "Furniture Installation", body: "Receiving, white-glove handling, placement and staging." },
            r8: { title: "Luxury Home Refreshes", body: "Curated mid-decade refreshes that protect resale value." },
            constructEyebrow: "Construction",
            constructTitle: "Construction oversight.",
            constructBody: "For ground-up builds and major capital projects, our team operates as your local project manager — independent of the contractor, focused entirely on your interests.",
            c1: { title: "Contractor Coordination", body: "Clear scopes, RFIs, and weekly meetings with all trades." },
            c2: { title: "Site Visits", body: "Scheduled and unannounced site inspections with photo logs." },
            c3: { title: "Progress Monitoring", body: "Schedule tracking, milestone verification, slippage flags." },
            c4: { title: "Quality Assurance", body: "Independent quality reviews at each phase of the build." },
            c5: { title: "Budget Tracking", body: "Pay-application review, change-order controls, contingency reporting." },
            c6: { title: "Vendor Communication", body: "A single bilingual point of contact between you and every trade." },
            c7: { title: "Owner Reporting", body: "Weekly photo-rich reports, financial summaries, and decision logs." },
            c8: { title: "Project Documentation", body: "Drawings, permits, warranties and closeout binders organized for you." },
            processEyebrow: "Process",
            processTitle: "A structured project cadence.",
            ph1: { title: "Project Planning", body: "Scope alignment, budget framing, and selection of the right delivery model." },
            ph2: { title: "Vendor Selection", body: "Curated bidding, reference checks, and contract review on your behalf." },
            ph3: { title: "Work Supervision", body: "Daily on-the-ground oversight, vendor coordination, and issue resolution." },
            ph4: { title: "Progress Reporting", body: "Weekly photo updates, financial tracking, and milestone reviews." },
            ph5: { title: "Quality Verification", body: "Independent walkthroughs, deficiency lists, and punch-list management." },
            ph6: { title: "Project Completion", body: "Final inspections, document closeout, and a clean handover to the owner." },
            reportEyebrow: "Reporting",
            reportTitle: "Detailed reporting & transparency.",
            reportBody: "Every project — large or small — runs on the same disciplined documentation framework. You will always know what is happening, what was decided, and what comes next.",
            rp1: { label: "Photo Reports", body: "Visual documentation of every site visit and milestone." },
            rp2: { label: "Progress Updates", body: "Weekly written summaries with photos and next steps." },
            rp3: { label: "Vendor Coordination", body: "Centralized communications, RFIs, and approval tracking." },
            rp4: { label: "Budget Tracking", body: "Live cost tracking against the approved budget, with change-order log." },
            rp5: { label: "Inspection Documentation", body: "Phase-end inspections logged with photos and findings." },
            whyEyebrow: "Why PKM",
            whyTitle: "Why homeowners trust PKM.",
            w1: { title: "Independent Oversight", body: "We are not the contractor. Our only loyalty is to your interests." },
            w2: { title: "Trusted Vendor Network", body: "A vetted bench of Los Cabos craftsmen, engineers and specialty trades." },
            w3: { title: "Detailed Communication", body: "Weekly reports, photo logs, and a single bilingual point of contact." },
            w4: { title: "Local Presence", body: "On-the-ground daily, in the communities and projects we serve." },
            w5: { title: "Luxury Property Experience", body: "Trained in the finishes, materials, and standards of luxury residences." },
            w6: { title: "Accountability Throughout the Process", body: "Documented decisions, signed approvals, transparent budgets." },
            outroEyebrow: "Planning a project?",
            outroTitle: "Planning a renovation or improvement project?",
            outroBody: "Let Pacific Key Management provide professional oversight and coordination while you enjoy peace of mind.",
            outroCta: "Schedule Consultation",
        },

        services_data: {
            "property-management": {
                title: "Property Management",
                summary: "Discreet, proactive oversight of luxury residences — from routine inspections to vendor supervision, executed with the standards of a private estate office.",
                bullets: ["Scheduled property inspections", "Preventative maintenance program", "Trusted vendor supervision", "Detailed photo-rich reporting"],
            },
            "estate-oversight": {
                title: "Estate Oversight",
                summary: "Specialized supervision of large private estates, with strict access protocols, inventory care, and structured operational standards.",
                bullets: ["Large residence supervision", "Inventory monitoring", "Access & key coordination", "Confidential reporting"],
            },
            "concierge": {
                title: "Concierge Services",
                summary: "From private chefs to yacht days, your arrival is prepared with hospitality-level care — every detail handled before you land.",
                bullets: ["Airport transfers & arrivals", "Private chefs & in-villa dining", "Yacht & golf reservations", "Wellness, spa & VIP experiences"],
            },
            "financial-administration": {
                title: "Financial Administration",
                summary: "Transparent oversight of property finances — utilities, HOA coordination, invoice management and structured monthly reporting.",
                bullets: ["Utility & HOA coordination", "Vendor invoice management", "Monthly financial reporting", "Bilingual accounting support"],
            },
            "rental-oversight": {
                title: "Luxury Rental Oversight",
                summary: "An elevated rental program for owners — curated guest preparation, immaculate arrival inspections, and post-stay reporting.",
                bullets: ["Guest arrival preparation", "Pre-stay & post-stay inspections", "Linen, amenities & turnover", "Owner statements & reporting"],
            },
            "hurricane-preparation": {
                title: "Hurricane Preparation",
                summary: "Structured seasonal protocols for storm preparation, securing your investment with thorough pre- and post-storm protocols.",
                bullets: ["Pre-season property audits", "Shutter & exterior securing", "Post-storm inspections", "24/7 emergency coordination"],
            },
            "maintenance-construction": {
                title: "Maintenance & Construction",
                summary: "Protecting and improving your residence through preventative maintenance, renovation oversight, vendor coordination, and construction supervision.",
                bullets: ["Preventative maintenance programs", "Renovation oversight", "Construction coordination", "Vendor supervision & reporting"],
            },
        },
        communities_data: {
            "palmilla": { tag: "Iconic Los Cabos legacy", description: "The original luxury enclave of Los Cabos — fairway-front estates, ocean villas, and a quietly storied address.", propertyTypes: ["Oceanfront estates", "Fairway villas", "Private residences"] },
            "villas-del-mar": { tag: "Refined gated residences", description: "A serene, gated community of private homes set against the Sea of Cortez — known for understated elegance and impeccable service.", propertyTypes: ["Beachfront villas", "Casitas", "Family estates"] },
            "querencia": { tag: "Member-only sanctuary", description: "A private golf and ocean club community — sculptural architecture, panoramic vistas, and a tranquil residential rhythm.", propertyTypes: ["Custom estates", "Golf villas", "Hilltop residences"] },
            "chileno-bay": { tag: "Sea & sky in dialogue", description: "A modern oceanfront residential club embracing one of Baja's most swimmable bays — architectural homes with cinematic views.", propertyTypes: ["Modern villas", "Bayfront homes", "Sky residences"] },
            "maravilla": { tag: "Cliffside elegance", description: "Perched along the Tourist Corridor with breathtaking ocean panoramas — a community of mature, beautifully crafted estates.", propertyTypes: ["Cliffside villas", "Ocean view homes", "Private residences"] },
            "el-dorado": { tag: "Private golf retreat", description: "A Nicklaus-designed golf community of refined, low-density residences — privacy, sport, and ocean breezes in perfect balance.", propertyTypes: ["Golf estates", "Ocean view villas", "Custom homes"] },
        },
        lifestyle_data: {
            villas: "Luxury Villas",
            chef: "Private Chef Experiences",
            yacht: "Yacht Days",
            golf: "Championship Golf",
            family: "Family Arrivals",
            ocean: "Ocean Lifestyle",
        },
        testimonialsPage: {
            eyebrow: "Pacific Key Management",
            heading: "Client Testimonials",
            quote: "The greatest measure of our success is the trust our clients place in us.",
            intro: "Hear from homeowners about their experience with Pacific Key Management and the care, communication and oversight we provide in Los Cabos.",
            filterAll: "All Testimonials",
            loading: "Loading…",
            emptyTitle: "Client stories, coming soon",
            emptyBody: "We are honored by the trust our homeowners place in us. Published client experiences will appear here.",
            seoTitle: "Client Testimonials | Pacific Key Management Los Cabos",
            seoDescription: "Hear from luxury homeowners about their experience with Pacific Key Management — the care, communication and oversight we provide in Los Cabos.",
        },
        sharePage: {
            eyebrow: "Pacific Key Management",
            heading: "Share Your Experience",
            intro: "Thank you for trusting Pacific Key Management with your home. We would greatly appreciate hearing about your experience with our team.",
            nameLabel: "Name",
            emailLabel: "Email",
            phoneLabel: "Phone",
            communityLabel: "Community",
            serviceLabel: "Service",
            experienceLabel: "Your Experience",
            selectOptional: "Select (optional)",
            photoLabel: "Client Photo (optional)",
            photoUploading: "Uploading…",
            photoAttached: "Attached",
            photoHint: "A photo is entirely optional and never required.",
            displayPrefLabel: "How would you like your name displayed if published?",
            showCommunityLabel: "May we display your community?",
            consentLabel: "I authorize Pacific Key Management to publish this testimonial on its website and marketing materials using the display preferences selected above.",
            submit: "Share My Experience",
            submitting: "Submitting…",
            consentError: "Please authorize publication to submit, or contact us directly.",
            photoTooLarge: "Image must be 5MB or smaller.",
            photoAdded: "Photo added.",
            uploadFailed: "Upload failed.",
            genericError: "Something went wrong. Please try again.",
            thankyouHeading: "Thank You",
            thankyouBody: "We sincerely appreciate you taking the time to share your experience with Pacific Key Management.",
            seoTitle: "Share Your Experience | Pacific Key Management",
            seoDescription: "Share your experience with Pacific Key Management.",
            prefFull: "Full name",
            prefFirstLast: "First name + last initial",
            prefFirst: "First name only",
            prefPrivate: "Private Homeowner",
            prefContact: "Please contact me before displaying my name",
        },
    },

    es: {
        nav: {
            home: "Inicio",
            about: "Nosotros",
            services: "Servicios",
            rentals: "Pacific Rentals",
            maintConstruct: "Mantenimiento y Construcción",
            concierge: "Concierge",
            blog: "Journal",
            testimonials: "Testimonios",
            contact: "Contacto",
            consultation: "Solicitar Consulta",
        },
        common: {
            requestConsultation: "Solicitar Consulta",
            exploreServices: "Explorar Servicios",
            learnMore: "Conocer Más",
            discover: "Descubrir",
            scrollDown: "Bajar",
            viewAll: "Ver Todas",
            inquire: "Consultar",
            sendInquiry: "Enviar Solicitud",
            sending: "Enviando",
        },
        form: {
            name: "Nombre Completo",
            email: "Correo Electrónico",
            phone: "Teléfono",
            propertyLocation: "Comunidad / Ubicación de la Propiedad",
            propertyLocationOptional: "Opcional",
            serviceInterest: "Servicio de Interés",
            message: "Mensaje",
            namePh: "Su nombre completo",
            emailPh: "usted@ejemplo.com",
            phonePh: "+1 (310) 000-0000",
            messagePh: "Cuéntenos algunos detalles sobre su residencia y lo que está buscando.",
            selectCommunity: "Seleccione una comunidad",
            selectService: "Seleccione un servicio",
            notSure: "Aún Indeciso / Evaluando Opciones",
            other: "Otro / No Listado",
            successTitle: "Gracias",
            successEyebrow: "Recibido",
            successBody: "Un miembro de nuestra oficina privada se pondrá en contacto en el plazo de un día hábil para coordinar su consulta.",
            sendAnother: "Enviar otra consulta",
            successToast: "Su solicitud de consulta ha sido recibida.",
            failToast: "No se pudo enviar. Por favor, intente de nuevo.",
        },
        footer: {
            tagline: "Gestión boutique de propiedades y patrimonios de lujo para los propietarios más exigentes en Los Cabos.",
            address: "Los Cabos · Baja California Sur · México",
            quickLinks: "Enlaces",
            services: "Servicios",
            contact: "Contacto",
            rights: "Todos los derechos reservados.",
            bilingual: "Servicio bilingüe · Inglés y Español",
            followUs: "Siga a Pacific Key Management",
        },
        home: {
            heroEyebrow: "Pacific Key Management",
            heroTitle1: "Protegiendo Su Inversión,",
            heroTitle2: "Elevando Su Estancia.",
            heroSubtitle: "Administración de propiedades y estates de lujo para residencias exclusivas en Los Cabos.",
            ctaConsult: "Solicitar Consulta",
            ctaServices: "Explorar Servicios",
            trustEyebrow: "Una Oficina Privada de Estates",
            trustTitle: "Excelencia operativa, entregada con discreción.",
            aboutEyebrow: "Una Filosofía Diferente",
            aboutTitle: "Una oficina boutique de estates privados para Los Cabos.",
            aboutBody1: "Existimos para un grupo selecto de propietarios que esperan que su propiedad opere con los estándares de un hotel de lujo — silenciosamente, con anticipación, y sin concesiones. Nuestro equipo combina cuidado preventivo, reportes estructurados y disciplina hospitalaria en una sola relación de confianza.",
            aboutBody2: "Desde la primera inspección hasta la llegada de su familia, cada detalle es manejado por personas que entienden las operaciones de lujo y la responsabilidad de proteger una inversión significativa.",
            aboutLink: "Nuestra Filosofía",
            servicesEyebrow: "Servicios",
            servicesTitle: "Discretamente integrales. Visiblemente considerados.",
            improvementsEyebrow: "Mejoras Inmobiliarias y Supervisión de Proyectos",
            improvementsTitle: "Protegiendo y elevando su inversión.",
            improvementsSubtitle: "Mantenimiento proactivo, supervisión de remodelaciones y coordinación de construcción de confianza — gestionados localmente, en su nombre.",
            communitiesEyebrow: "Comunidades",
            communitiesTitle: "Servimos las direcciones más distinguidas de Los Cabos.",
            communitiesCta: "Ver Todas las Comunidades",
            lifestyleEyebrow: "El Estilo de Vida en Cabo",
            lifestyleTitle: "Curado para la forma en que realmente vive aquí.",
            whyEyebrow: "Por Qué PKM",
            whyTitle: "El estándar de una oficina privada de estates.",
            testimonialsEyebrow: "En Sus Palabras",
            testimonialsTitle: "La confianza de propietarios en todo Los Cabos.",
            trustedEyebrow: "En Sus Palabras",
            trustedTitle: "La Confianza de Nuestros Propietarios",
            trustedBody: "Relaciones construidas con confianza, comunicación y un cuidado excepcional.",
            trustedCta: "Leer Testimonios",
            ctaEyebrow: "Comencemos",
            ctaTitle: "Solicite una consulta privada.",
            ctaBody: "Comparta algunos detalles sobre su residencia. Un miembro de nuestro equipo dará seguimiento en un día hábil para coordinar una conversación privada — en su villa, por teléfono o video.",
            ctaDirect: "Directo",
            ctaEmail: "Correo",
        },
        trust: {
            specialists: "Especialistas en Comunidades de Lujo",
            experience: "Más de 10 Años de Experiencia Local",
            support: "Soporte de Emergencia 24 / 7",
            preventative: "Supervisión Preventiva de Propiedades",
            bilingualLabel: "Comunicación Bilingüe",
            transparent: "Coordinación Transparente de Proveedores",
            since1996: "Residente de Los Cabos desde 1996",
            since2015: "Administración de Propiedades de Lujo desde 2015",
            bilingual: "Bilingüe Inglés y Español",
            emergency: "Soporte de Emergencia 24/7",
            boutique: "Enfoque de Portafolio Boutique",
        },
        improvements: {
            preventative: { title: "Mantenimiento Preventivo", body: "Programas de cuidado calendarizado diseñados para detectar pequeños problemas antes de que se conviertan en gastos mayores." },
            renovation: { title: "Supervisión de Remodelaciones", body: "Desde renovaciones de cocina hasta actualizaciones integrales de interiores, PKM supervisa proveedores, tiempos y calidad en nombre del propietario." },
            construction: { title: "Coordinación de Construcción", body: "Representación local del propietario durante proyectos de construcción — visitas de obra, reportes de avance y comunicación con contratistas." },
            vendors: { title: "Supervisión de Proveedores", body: "Profesionales de confianza, coordinados y supervisados con comunicación clara, documentación y responsabilidad." },
        },
        whyPkm: {
            proactive: { title: "Supervisión Proactiva", body: "Problemas identificados y resueltos antes de que le lleguen. Programados, estructurados y silenciosamente minuciosos." },
            boutique: { title: "Atención Boutique", body: "Un portafolio selecto de residencias — nunca impulsado por volumen. Cada hogar recibe la profundidad de atención que merece." },
            vendors: { title: "Red de Proveedores de Confianza", body: "Socios locales curados y verificados en mantenimiento, hospitalidad y oficios especializados — supervisados en su nombre." },
            reporting: { title: "Reportes Estructurados", body: "Reportes mensuales con fotografías, transparencia financiera y comunicación clara en inglés o español." },
            response: { title: "Tiempos de Respuesta Rápidos", body: "Un equipo disponible para emergencias, escalamientos con proveedores y necesidades de llegada de último momento — en cualquier momento." },
            standards: { title: "Estándares de Hospitalidad de Lujo", body: "Formados en la cultura operativa de los resorts líderes del mundo — aplicados en privado a su hogar." },
        },
        testimonials: {
            t1: { quote: "Lo que más valoramos es la comunicación. Siempre sabemos qué está pasando en la casa, y los problemas se atienden antes de que se conviertan en algo mayor.", author: "Propietario Privado", location: "Villas del Mar" },
            t2: { quote: "Viviendo fuera de México, necesitábamos a alguien en quien pudiéramos confiar localmente. Fernando y su equipo nos han dado esa tranquilidad.", author: "Propietario Estacional", location: "Palmilla" },
            t3: { quote: "Desde la coordinación con proveedores hasta la preparación para huracanes, PKM mantiene todo organizado y claro. Ese nivel de supervisión es exactamente lo que necesitábamos.", author: "Cliente Privado", location: "Los Cabos" },
        },
        about: {
            heroEyebrow: "Nosotros",
            heroTitle1: "Una oficina privada de estates —",
            heroTitle2: "redefinida para Cabo.",
            heroSubtitle: "Pacific Key Management fue fundada sobre una premisa simple: los propietarios más distinguidos de Los Cabos merecen los estándares operativos de los resorts líderes del mundo — aplicados de manera privada a sus residencias.",
            storyEyebrow: "Nuestra Historia",
            storyTitle: "Construidos en silencio. Operando con meticulosidad.",
            storyP1: "Pacific Key comenzó con un pequeño portafolio cuidadosamente seleccionado de residencias en Palmilla y Villas del Mar — hogares cuyos propietarios requerían un nivel de atención que el modelo convencional de administración de propiedades simplemente no podía ofrecer.",
            storyP2: "Más de una década después, seguimos siendo deliberadamente boutique. Solo aceptamos residencias cuando podemos brindarles la profundidad de cuidado que requieren — nunca impulsados por volumen, siempre guiados por la relación.",
            storyP3: "Hoy, nuestro equipo opera en las comunidades cerradas más prestigiosas de Los Cabos, combinando disciplina hospitalaria, ingeniería preventiva y hospitalidad discreta para proteger los hogares — y las vidas — que nos son confiados.",
            founderEyebrow: "La Promesa del Fundador",
            founderTitle: "\"Su residencia será cuidada como si fuera la nuestra.\"",
            founderP1: "Pacific Key fue fundada por profesionales de la hospitalidad con profunda experiencia operativa en los principales resorts y clubes privados de Los Cabos. Llevamos esa cultura — servicio refinado, operaciones estructuradas, cuidado anticipatorio — a los hogares privados que servimos.",
            founderP2: "Cada relación con propietarios comienza con una conversación privada, una evaluación honesta de lo que su residencia necesita, y un plan operativo claro adaptado a ella.",
            // Conozca al Equipo
            teamEyebrow: "Conozca al Equipo",
            teamTitle: "Las personas detrás de Pacific Key Management.",
            teamLead: "Una empresa boutique de administración de estates dirigida por personas personalmente comprometidas con proteger su hogar.",
            team1Name: "Luis Fernando Mendoza",
            team1Title: "Fundador y Director General",
            team1Initials: "LM",
            team1P1: "Luis Fernando Mendoza ha llamado hogar a Los Cabos desde 1996 y se ha especializado en administración de propiedades de lujo y estates desde 2015.",
            team1P2: "Con una formación arraigada en hospitalidad, servicio al cliente y operaciones de estates privados, Luis aporta un enfoque práctico a cada propiedad bajo el cuidado de Pacific Key Management.",
            team1P3: "Habiendo trabajado en comunidades prestigiosas como Palmilla, Villas del Mar y Querencia, comprende las expectativas únicas de los propietarios de lujo y considera que el mantenimiento preventivo, la comunicación proactiva y la atención al detalle son la base de una administración de propiedades excepcional.",
            team1P4: "Como residente de largo plazo, hombre de familia y recurso local de confianza, Luis se mantiene personalmente involucrado en inspecciones, comunicación con propietarios, supervisión de proyectos y coordinación de concierge.",
            team2Name: "Fernando Mendoza Rocha",
            team2Title: "Coordinador de Operaciones y Contabilidad",
            team2Initials: "FM",
            team2P1: "Fernando Mendoza Rocha sostiene la columna vertebral operativa y administrativa de Pacific Key Management.",
            team2P2: "Sus responsabilidades incluyen seguimiento financiero, reportes, coordinación de proveedores, programación, contabilidad y asegurar que las operaciones diarias se ejecuten de manera eficiente y transparente.",
            team2P3: "Trabajando estrechamente con propietarios, proveedores y prestadores de servicios, Fernando ayuda a mantener los sistemas estructurados y los estándares de comunicación por los que Pacific Key Management es reconocida.",
            team3Name: "Red de Proveedores de Confianza",
            team3Title: "Aliados en Mantenimiento, Construcción y Concierge",
            team3P1: "Pacific Key Management trabaja junto a una red cuidadosamente seleccionada de profesionales con licencia, contratistas, especialistas en housekeeping, proveedores de concierge, jardineros, técnicos de piscina y aliados de hospitalidad en todo Los Cabos.",
            team3P2: "Cada proveedor se selecciona con base en confiabilidad, profesionalismo, capacidad de respuesta y desempeño comprobado en entornos residenciales de lujo.",
            stat1Number: "1996",
            stat1Label: "Experiencia Local en Los Cabos",
            stat2Number: "2015",
            stat2Label: "Administración de Propiedades de Lujo",
            stat3Number: "Bilingüe",
            stat3Label: "Comunicación en Inglés y Español",
            stat4Number: "Boutique",
            stat4Label: "Portafolio Basado en Relaciones",
            valuesEyebrow: "Nuestros Valores",
            valuesTitle: "Estándares silenciosos que nunca comprometemos.",
            v1: { title: "Discreción", body: "Nos movemos en silencio por su hogar. La privacidad es la base de cada protocolo que operamos." },
            v2: { title: "Excelencia", body: "Formados en hospitalidad de lujo, nuestros estándares exceden las convenciones de la administración de propiedades." },
            v3: { title: "Confianza", body: "Nos ganamos el derecho de proteger su inversión a través de la transparencia, la consistencia y el cuidado." },
            v4: { title: "Maestría Local", body: "Décadas de experiencia combinada dentro de las comunidades privadas más prestigiosas de Los Cabos." },
            standardsEyebrow: "Estándares Operativos",
            standardsTitle: "El ritmo detrás de una residencia perfectamente administrada.",
            standardsBody: "Detrás de cada hogar Pacific Key hay una cadencia operativa estructurada — diseñada para detectar problemas antes de que surjan, y para ofrecer la consistencia que exige una residencia de lujo.",
            s1: { label: "Inspecciones", body: "Rutinarias, estructuradas, documentadas con fotografías." },
            s2: { label: "Reportes", body: "Mensuales, transparentes, bilingües." },
            s3: { label: "Proveedores", body: "Verificados, asegurados, supervisados en sitio." },
            s4: { label: "Respuesta", body: "Disponibles 24 / 7 para emergencias y llegadas." },
            outroTitle: "¿Considerando Pacific Key?",
            outroBody: "Aceptamos un número limitado de residencias cada año. Contáctenos para una conversación privada.",
        },
        services: {
            heroEyebrow: "Servicios",
            heroTitle1: "Cuidado integral,",
            heroTitle2: "ejecutado con discreción.",
            heroSubtitle: "Cada servicio está diseñado con los estándares de una oficina privada de estates. Elija servicios individuales o nuestro programa completo de administración.",
            sectionEyebrow: "Servicio",
            processEyebrow: "Proceso",
            processTitle: "De la primera consulta a la administración continua.",
            p1: { title: "Consulta Privada", body: "Una conversación confidencial sobre su residencia, expectativas y necesidades actuales." },
            p2: { title: "Auditoría de la Propiedad", body: "Visita en sitio, evaluación de condiciones y plan operativo a la medida." },
            p3: { title: "Cadencia Operativa", body: "Inspecciones programadas, calendario de proveedores, estructura de reportes y servicios concierge base." },
            p4: { title: "Administración Continua", body: "Reportes mensuales transparentes, respuesta rápida, cuidado anticipatorio — mes tras mes." },
            outroEyebrow: "A la Medida de Su Residencia",
            outroTitle: "Diseñe su plan operativo.",
            outroBody: "Cada plan es a la medida. Cuéntenos sobre su residencia y estructuraremos el alcance adecuado.",
        },
        rentals: {
            // Hero
            heroEyebrow: "Pacific Rentals",
            heroTitle1: "Hogares Excepcionales.",
            heroTitle2: "Gestionados Personalmente.",
            heroSubtitle: "Una colección curada de residencias privadas excepcionales en Los Cabos, gestionadas profesionalmente y con apoyo local de Pacific Key Management.",
            heroCtaFeatured: "Conocer Hacienda 511",
            heroCtaPlan: "Planifique Su Estancia",

            // Por Qué Reservar Directo
            directEyebrow: "Por Qué Reservar Directo",
            directTitle: "Las ventajas de reservar directamente.",
            directSubtitle: "Reservar su estancia directamente con Pacific Key Management significa una experiencia más personal, desde la primera consulta hasta la salida.",
            direct_rate: "La mejor tarifa directa disponible",
            direct_planning: "Planificación personalizada previa a la llegada",
            direct_support: "Apoyo local durante toda su estancia",
            direct_concierge: "Acceso directo a nuestro equipo concierge",
            direct_nofees: "Sin cargos de plataformas de reserva",

            // Sección 2 — Residencia Destacada
            featuredEyebrow: "Residencia Destacada",
            featuredTitle: "Presentando Hacienda 511.",
            featuredSubtitle: "Nuestra experiencia de villa de lujo insignia en Villas del Mar.",
            featuredViewCta: "Ver Residencia",
            featuredFlagship: "Residencia Insignia",

            // Sección 3 — La Experiencia Pacific Rentals
            experienceEyebrow: "La Experiencia Pacific Rentals",
            experienceTitle: "Sin esfuerzo, de la llegada a la despedida.",
            experienceBody: "Creemos que el viaje de lujo es más que la residencia misma. Cada estancia debe sentirse sin esfuerzo, personalizada y memorable, desde la llegada hasta la despedida.",
            exp_chef: "Chefs Privados",
            exp_butler: "Servicio de Mayordomo",
            exp_bartender: "Bartender y Mixología",
            exp_grocery: "Aprovisionamiento de Despensa",
            exp_transport: "Transporte Privado",
            exp_golf: "Concierge de Golf",
            exp_yacht: "Charters de Yate",
            exp_spa: "Spa y Bienestar",

            // Sección 4 — Por Qué los Huéspedes Eligen Pacific Rentals
            whyEyebrow: "Por Qué los Huéspedes Eligen Pacific Rentals",
            whyTitle: "Discretamente distintos a cualquier plataforma de reservación.",
            why_curated: { title: "Colección Curada", body: "Cada residencia se selecciona según ubicación, calidad, estándares de mantenimiento y experiencia para el huésped." },
            why_concierge: { title: "Concierge Personalizado", body: "Experiencias a la medida antes y durante su estancia." },
            why_standards: { title: "Estándares de Hospitalidad de Lujo", body: "Atención al detalle inspirada en los resorts líderes del mundo." },
            why_local: { title: "Experiencia Local", body: "Un equipo basado en Los Cabos con amplio conocimiento local y relaciones de confianza." },

            // Sección 5 — Nuestros Estándares de Colección
            standardsEyebrow: "Nuestros Estándares de Colección",
            standardsTitle: "No toda villa califica para Pacific Rentals.",
            standardsBody: "Evaluamos cuidadosamente cada residencia con base en:",
            c_location: "Ubicación",
            c_design: "Diseño y Arquitectura",
            c_privacy: "Privacidad",
            c_maintenance: "Estándares de Mantenimiento",
            c_owner: "Compromiso del Propietario",
            c_guest: "Experiencia para el Huésped",
            c_concierge: "Potencial de Concierge",
            standardsClosing: "Algunas residencias son administradas directamente por Pacific Key Management, mientras que otras se representan a través de alianzas cuidadosamente seleccionadas que se alinean con nuestra filosofía de hospitalidad.",

            // Sección 6 — Próximamente
            comingEyebrow: "Próximamente",
            comingTitle: "Una colección en evolución.",
            comingSubtitle: "Se irán incorporando residencias adicionales de lujo a nuestra colección a medida que continuamos expandiendo nuestro portafolio en las comunidades más prestigiosas de Los Cabos.",
            comingLabel: "Próximamente",

            // Sección 7 — Para Propietarios
            ownerEyebrow: "Para Propietarios",
            ownerTitle: "Genere ingresos sin sacrificar su tranquilidad.",
            ownerBody: "Para propietarios que desean rentar su residencia bajo sus propios términos — sin comprometer la privacidad, la condición o el cuidado. Nuestro programa es la antítesis de las plataformas de alto volumen.",
            ownerCta: "Inscribir Su Residencia",
            ob1: { title: "Programa Centrado en el Propietario", body: "Su hogar, sus fechas. Trabajamos en torno a su uso y protegemos el activo entre estancias." },
            ob2: { title: "Solo Huéspedes Verificados", body: "Investigación discreta, verificación de identidad y calendarios de reservación curados." },
            ob3: { title: "Operación Hospitalaria", body: "Personal entrenado en resorts, limpieza impecable y llegadas con provisiones preparadas." },
            ob4: { title: "Reportes Transparentes", body: "Estados de cuenta para propietarios, métricas de desempeño y revisiones trimestrales del portafolio." },

            // CTA Final
            outroEyebrow: "Planifique Su Escape a Los Cabos",
            outroTitle: "Cuéntenos sobre su estancia.",
            outroBody: "Cuéntenos sobre sus fechas de viaje y preferencias, y nuestro equipo le ayudará a curar la experiencia de villa perfecta.",
            outroCtaAvailability: "Consultar Disponibilidad",
            outroCtaConcierge: "Contactar al Concierge",

            // Compartido / Detalle de Villa
            collectionEyebrow: "La Colección",
            collectionTitle: "La Colección Pacific Rentals.",
            curated: "Curada",
            bedroomsShort: "Recámaras",
            bathsShort: "Baños",
            powderShort: "Medios Baños",
            sleepsShort: "Huéspedes",
            viewVilla: "Ver Residencia",
            villaBack: "Regresar a la Colección",
            villaHighlights: "Aspectos Destacados",
            villaInquire: "Consultar Sobre Esta Villa",
            villaNotFound: "Villa no encontrada",
            villaAmenities: "Amenidades",
            villaPlaceholder: "Fotografía ilustrativa · portafolio de villas en expansión",
        },
        villa: {
            // CTAs compartidos
            requestAvailability: "Consultar Disponibilidad",
            checkAvailability: "Consultar Disponibilidad",
            requestToBook: "Solicitar Reserva",
            whatsappConcierge: "Concierge por WhatsApp",
            downloadBrochure: "Descargar Brochure",
            speakConcierge: "Concierge por WhatsApp",

            // Sección 2
            retreatEyebrow: "Un Retiro Extraordinario en Los Cabos",
            retreatTitle: "Un retiro extraordinario en Los Cabos.",
            retreatP1: "Enclavada dentro de la prestigiosa comunidad de Villas del Mar, Hacienda 511 combina la arquitectura mexicana atemporal, una expansiva vida interior-exterior y amenidades excepcionales para crear un retiro privado inolvidable.",
            retreatP2: "Diseñada para reuniones familiares, celebraciones y escapadas de lujo, cada espacio invita a la relajación, la conexión y experiencias memorables en Los Cabos.",

            // Sección 3
            whyEyebrow: "Por Qué los Huéspedes Aman Hacienda 511",
            whyTitle: "Diseñada para momentos memorables.",
            f_theater: { title: "Cine Privado", body: "Noches de cine en familia en una experiencia de cinema dedicada." },
            f_pool: { title: "Alberca y Jacuzzi", body: "Diseñadas para largos días bajo el sol de Baja." },
            f_firepit: { title: "Lounge con Firepit", body: "Reúnanse para cócteles al atardecer y conversaciones nocturnas." },
            f_outdoor: { title: "Vida al Aire Libre", body: "Múltiples espacios de comedor y lounge para entretener." },
            f_golfCart: { title: "Carrito de Golf Incluido", body: "Transporte cómodo a lo largo de la comunidad." },
            f_concierge: { title: "Servicio Concierge Privado", body: "Planeación personalizada antes y durante su estancia." },

            // Sección 4
            galleryEyebrow: "Galería de Estilo de Vida",
            galleryTitle: "La vida en Hacienda 511.",
            galleryBody: "Una mirada inmersiva a los espacios, la luz y el ritmo de una estancia.",
            viewAllPhotos: "Ver todas las fotos",

            // También conocida como — alias reconocido de mercado/búsqueda (misma residencia)
            alsoKnownAs: "Hacienda 511 también es conocida como Villa Bravura del Mar — una residencia privada de siete recámaras en Villas del Mar, Los Cabos.",

            // Incluido en Su Estancia
            includedEyebrow: "Incluido en Su Estancia",
            includedTitle: "Parte de la experiencia de la residencia.",
            includedBody: "Cada estancia en Hacienda 511 incluye atención diaria y fácil acceso dentro de la comunidad. Los huéspedes que reservan directamente con Pacific Key Management también disfrutan de servicio de mayordomo y un chef privado para desayuno y comida.",
            inc_housekeeping: { title: "Limpieza Diaria", body: "Servicio de limpieza diario y atento durante toda su estancia." },
            inc_golfCart: { title: "Un Carrito de Golf", body: "Un carrito de golf dedicado para desplazarse por Villas del Mar." },
            inc_butler: { title: "Servicio de Mayordomo", body: "Servicio de mayordomo en la villa para atender sus necesidades diarias." },
            inc_chef: { title: "Chef Privado · Desayuno y Comida", body: "Un chef privado prepara el desayuno y la comida en la villa cada día." },
            badgeIncluded: "Incluido",
            badgeDirect: "Reservaciones Directas PKM",
            includedNote: "La limpieza diaria y un carrito de golf están incluidos en cada estancia. El servicio de mayordomo y el chef privado para desayuno y comida se incluyen en reservaciones hechas directamente con Pacific Key Management.",

            // Experiencias Personalizadas (opcionales — bajo solicitud)
            personalizedEyebrow: "Experiencias Personalizadas",
            personalizedTitle: "Coordinadas con cuidado en torno a su estancia.",
            personalizedBody: "Además de lo incluido, nuestro equipo de concierge puede coordinar diversas experiencias opcionales a la medida de su grupo — desde cenas privadas hasta días en el mar.",
            px_dinnerChef: "Chef Privado para Cenas",
            px_bartender: "Bartender y Mixólogo",
            px_grocery: "Abastecimiento de Despensa",
            px_transport: "Transporte Privado y Traslados",
            px_golf: "Concierge de Golf",
            px_yacht: "Charters de Yate",
            px_spa: "Spa y Bienestar",
            px_dining: "Reservaciones en Restaurantes",
            personalizedNote: "Las experiencias opcionales se coordinan bajo solicitud y no están incluidas en la tarifa por noche.",

            // Sección 5
            elevateEyebrow: "Eleve Su Estancia",
            elevateTitle: "Experiencias personalizadas, arregladas con discreción.",
            elevateBody: "Nuestro equipo concierge puede coordinar experiencias personalizadas diseñadas en torno a sus preferencias.",
            e_chef: "Chefs Privados",
            e_butler: "Servicio de Mayordomo",
            e_bartender: "Bartender y Mixología",
            e_grocery: "Aprovisionamiento de Despensa",
            e_transport: "Transporte Privado",
            e_golf: "Concierge de Golf",
            e_yacht: "Charters de Yate",
            e_spa: "Spa y Bienestar",

            // Sección 6 — Su Estancia, a Su Manera (experiencias personalizadas)
            itineraryEyebrow: "Experiencias Curadas",
            itineraryTitle: "Tu estancia, a tu manera",
            itineraryBody: "Cada estancia en Hacienda 511 se diseña en torno a usted — nunca es un paquete predefinido. Considere estas experiencias curadas, coordinadas por nuestro equipo concierge según el ritmo y las preferencias de su grupo.",
            exp_chef: { title: "Cena con Chef Privado", body: "Menús en la villa creados por un chef privado, desde almuerzos junto a la alberca hasta elegantes cenas de varios tiempos." },
            exp_pool: { title: "Días de Alberca y Villa", body: "Días sin prisa entre la alberca infinita, el jacuzzi y las terrazas, con servicio completo de villa." },
            exp_spa: { title: "Spa y Bienestar", body: "Masajes en la residencia, rituales de bienestar y entrenamiento personal a su conveniencia." },
            exp_yacht: { title: "Charters de Yate", body: "Un día privado en el Mar de Cortés con provisiones curadas y tripulación." },
            exp_golf: { title: "Golf", body: "Horarios de salida en los célebres campos de campeonato de la región, coordinados para su grupo." },
            exp_beach: { title: "Playa y Costa", body: "Playas para nadar, escapadas costeras y atardeceres a lo largo del corredor de Los Cabos." },
            exp_dining: { title: "Gastronomía en Los Cabos", body: "Reservaciones y transporte a los mejores restaurantes de San José y Cabo San Lucas." },
            exp_concierge: { title: "Concierge Personalizado", body: "Celebraciones, traslados y solicitudes a la medida — atendidos con esmero de principio a fin." },

            // Sección 7 — Habitaciones
            accomEyebrow: "Habitaciones",
            accomTitle: "Siete recámaras. Diseñada para catorce huéspedes.",
            accomBody: "Siete recámaras, siete baños completos y dos medios baños — con capacidad para hasta catorce huéspedes.",
            b1: { label: "Suite Principal", body: "Una cama king." },
            b2: { label: "Segunda Suite Principal", body: "Una cama king." },
            b3: { label: "Suite de Huéspedes I", body: "Una cama king." },
            b4: { label: "Suite de Huéspedes II", body: "Una cama king." },
            b5: { label: "Suite Familiar", body: "Dos camas queen." },
            b6: { label: "Suite Infantil", body: "Una cama matrimonial y una individual." },
            b7: { label: "Casita", body: "Dos camas queen." },
            countBedrooms: "Recámaras",
            countBaths: "Baños",
            countSleeps: "Capacidad de Huéspedes",

            // Sección 8 — Tarifas
            ratesEyebrow: "Tarifas",
            ratesTitle: "Tarifas por Temporada",
            ratesNote: "Las tarifas se cotizan en USD por noche y están sujetas a disponibilidad, más impuestos y cargos aplicables.",
            minStayNote: "Estancia mínima: 3 noches estándar · 5 noches en Acción de Gracias · 7 noches en Navidad y Año Nuevo.",
            r_low: { label: "Temporada Baja", period: "Mayo – Octubre" },
            r_high: { label: "Temporada Alta", period: "Noviembre – Abril" },
            r_holiday: { label: "Temporada Festiva", period: "Acción de Gracias · Semana Santa" },
            r_festive: { label: "Navidad / Año Nuevo", period: "20 de Diciembre – 5 de Enero" },
            rateSuffix: "USD / Noche",

            // Sección 9 — Diferencia
            differenceEyebrow: "La Diferencia Pacific Rentals",
            differenceTitle: "Hogares excepcionales. Estancias inolvidables.",
            differenceP1: "Cada residencia dentro de nuestra colección es seleccionada por su calidad, ubicación, estándares de mantenimiento y experiencia para el huésped.",
            differenceP2: "Ya sea administrada directamente por Pacific Key Management o representada a través de alianzas de confianza, nuestro objetivo se mantiene: hogares excepcionales y estancias inolvidables en Los Cabos.",

            // Sección 10 — Consulta
            inquireEyebrow: "Consulta de Disponibilidad",
            inquireTitle: "Comience a planificar su estancia.",
            inquireBody: "Comparta sus fechas preferidas y tamaño de grupo, y un miembro de nuestro equipo concierge responderá en un día hábil.",
            f_dates: "Fechas Preferidas",
            f_datesPh: "ej. 22 – 29 Dic",
            f_guests: "Número de Huéspedes",
            f_guestsPh: "¿Cuántos huéspedes?",
            f_special: "Solicitudes Especiales",
            f_specialPh: "Aniversarios, accesibilidad, preferencias dietéticas…",
            f_message: "Mensaje",
            f_messagePh: "Cuéntenos sobre su estancia.",
            contactPhone: "Teléfono",
            contactWhatsapp: "WhatsApp",
            contactEmail: "Correo",
        },
        communities: {
            heroEyebrow: "Comunidades",
            heroTitle1: "Las direcciones más distinguidas",
            heroTitle2: "de Los Cabos.",
            heroSubtitle: "Desde la histórica Palmilla hasta el moderno frente al mar de Chileno Bay, operamos con profundidad dentro de las comunidades que definen la vida de lujo en Baja.",
            propertyTypes: "Tipos de Propiedad",
            pkmApproach: "Enfoque de Soporte PKM",
            pkmApproachBody: "Especialista dedicado a la comunidad · inspecciones rutinarias en sitio · red de proveedores locales verificados · coordinación con la HOA específica de la comunidad · protocolos centrados en la privacidad.",
            outroTitle: "¿Propietarios en otra comunidad?",
            outroBody: "Nuestro portafolio se extiende más allá de estos enclaves emblemáticos. Contáctenos — es posible que ya sirvamos su dirección.",
            outroCta: "Consultar Sobre Su Comunidad",
        },
        concierge: {
            // Hero
            heroEyebrow: "Concierge",
            heroTitle1: "Servicios de Concierge",
            heroTitle2: "Personalizados.",
            heroSubtitle: "Desde chefs privados y charters de yate hasta reservaciones de golf y experiencias familiares, Pacific Key Management ofrece servicios a la medida, diseñados en torno a su estilo de vida.",
            heroCta: "Planifique Su Estancia",

            // Sección 1 — Experiencias Curadas
            introEyebrow: "Una Filosofía de Servicio",
            introTitle: "Experiencias curadas, hechas a su medida.",
            introBody: "Cada propietario y huésped tiene prioridades distintas. Ya sea que busque descanso, aventura, actividades familiares, bienestar o acceso exclusivo, nuestro equipo concierge coordina cada detalle antes y durante su estancia.",
            introTagline: "Nosotros nos encargamos de cada detalle — para que usted disfrute Los Cabos.",

            // Sección 2 — Gastronomía y Hospitalidad
            diningEyebrow: "Gastronomía y Hospitalidad Privada",
            diningTitle: "Una experiencia hospitalaria en su villa.",
            d1: { title: "Chefs Privados", bullets: ["Desayunos, comidas y cenas en su villa", "Menús de degustación de varios tiempos", "Comidas familiares al estilo casero", "Celebraciones especiales"] },
            d2: { title: "Servicio de Mayordomo", bullets: ["Asistencia personalizada diaria", "Servicio de bebidas", "Atención al huésped", "Apoyo en la villa durante toda la estancia"] },
            d3: { title: "Bartender y Mixología", bullets: ["Cócteles junto a la alberca", "Eventos privados", "Maridajes con vino", "Experiencias de autor"] },
            d4: { title: "Aprovisionamiento de Despensa", bullets: ["Villas listas a su llegada", "Listas de compras personalizadas", "Atención a requerimientos dietéticos", "Solicitudes premium de vinos y destilados"] },
            d5: { title: "Planeación de Ocasiones Especiales", bullets: ["Cumpleaños", "Aniversarios", "Celebraciones familiares", "Retiros corporativos"] },

            // Sección 3 — Mar y Yates
            oceanEyebrow: "Experiencias en el Mar y Yates",
            oceanTitle: "El Mar de Cortés, a su manera.",
            oceanBody: "Desde charters al amanecer hasta cruceros al atardecer, nuestras experiencias marítimas se coordinan con capitanes y tripulaciones verificados — aprovisionadas a su gusto y a la medida de su grupo.",
            o1: { title: "Charters Privados de Yate", bullets: ["Cruceros al atardecer", "Charters de lujo de día completo", "Salidas multifamiliares"] },
            o2: { title: "Pesca Deportiva", bullets: ["Marlín", "Atún", "Dorado", "Excursiones de temporada"] },
            o3: { title: "Catamaranes de Lujo", bullets: ["Experiencias familiares", "Aventuras de snorkel", "Exploración costera"] },
            o4: { title: "Avistamiento de Ballenas", bullets: ["Tours privados de temporada"] },
            o5: { title: "Snorkel y Buceo", bullets: ["Los mejores sitios marinos de Cabo"] },
            o6: { title: "Deportes Acuáticos", bullets: ["Paddleboard", "Kayak", "Experiencias en jet ski"] },

            // Sección 4 — Golf
            golfEyebrow: "Experiencias de Golf",
            golfTitle: "Algunos de los mejores campos de Norteamérica.",
            golfBody: "Los Cabos alberga campos de campeonato diseñados por Nicklaus, Norman, Tiger Woods y Davis Love III — Diamante, El Dorado, Querencia, Quivira, Cabo del Sol y más. Nuestro equipo coordina cada detalle para que su día sea tan fluido como su swing.",
            g1: "Reservación de tee times",
            g2: "Transporte privado",
            g3: "Renta de palos de golf",
            g4: "Coordinación de torneos",
            g5: "Itinerarios de varios campos",

            // Sección 5 — Bienestar
            wellnessEyebrow: "Bienestar y Relajación",
            wellnessTitle: "Rituales llevados a su residencia.",
            w1: { title: "Spa en la Villa", bullets: ["Masajes terapéuticos", "Faciales", "Tratamientos corporales"] },
            w2: { title: "Sesiones Privadas de Fitness", bullets: ["Entrenamiento personal", "Yoga", "Pilates"] },
            w3: { title: "Retiros de Bienestar", bullets: ["Programas de varios días", "Planeación nutricional", "Experiencias holísticas"] },
            w4: { title: "Servicios de Belleza", bullets: ["Estilismo de cabello", "Maquillistas profesionales", "Servicios de grooming"] },

            // Sección 6 — Familia y Estilo de Vida
            familyEyebrow: "Servicios para Familia y Estilo de Vida",
            familyTitle: "Logística impecable para toda la familia.",
            f1: "Niñeras y Cuidado Infantil",
            f2: "Planeación de Actividades Familiares",
            f3: "Transporte Privado",
            f4: "Traslados al Aeropuerto",
            f5: "Renta de Vehículos de Lujo",
            f6: "Coordinación de Carros de Golf",
            f7: "Asistencia con Mascotas",
            f8: "Asistencia Personal de Compras",

            // Sección 7 — Experiencias Exclusivas
            exclusiveEyebrow: "Experiencias Exclusivas de Los Cabos",
            exclusiveTitle: "Más allá de la guía turística.",
            exclusiveBody: "Experiencias memorables y fuera del itinerario, arregladas a mano — extraídas de nuestra red profunda de artistas, capitanes, sommeliers, fotógrafos y guías locales.",
            ex1: "Art Walk de San José",
            ex2: "Tours culturales privados",
            ex3: "Experiencias culinarias",
            ex4: "Aventuras en el desierto",
            ex5: "Excursiones en ATV",
            ex6: "Paseos en camello",
            ex7: "Asistencia de compras de lujo",
            ex8: "Sesiones de fotografía",
            ex9: "Experiencias privadas en la playa",

            // Sección 8 — Negocios y Ejecutivo
            businessEyebrow: "Soporte Ejecutivo y de Negocios",
            businessTitle: "Cuando los negocios se encuentran con Baja.",
            businessBody: "Para propietarios y huéspedes que combinan negocios con viaje, nuestro equipo se integra a la perfección con su oficina — coordinando la logística con la misma discreción que aplicamos en su vida privada.",
            b1: "Transporte ejecutivo",
            b2: "Coordinación de retiros corporativos",
            b3: "Coordinación de juntas",
            b4: "Asistencia administrativa",
            b5: "Apoyo en planeación de eventos",

            // Sección 9 — Por Qué PKM
            whyEyebrow: "Por Qué los Huéspedes Eligen PKM Concierge",
            whyTitle: "El estándar de una oficina privada de estilo de vida.",
            why1: { title: "Experiencia Local", body: "Décadas combinadas de experiencia dentro de las comunidades y recintos más codiciados de Los Cabos." },
            why2: { title: "Red de Proveedores de Confianza", body: "Una banca verificada de chefs privados, capitanes, terapeutas de spa y socios de estilo de vida." },
            why3: { title: "Recomendaciones Personalizadas", body: "Hechas a la medida de su grupo, su gusto y el ritmo de su estancia." },
            why4: { title: "Comunicación Confiable", body: "Un único punto de contacto, ágil entre zonas horarias — antes, durante y después." },
            why5: { title: "Estándares de Hospitalidad de Lujo", body: "Formados en la cultura operativa de los resorts y clubes privados líderes del mundo." },
            why6: { title: "Coordinación Impecable", body: "Cada proveedor, traslado y reservación orquestados con discreción tras bambalinas." },
            why7: { title: "Soporte Bilingüe", body: "Inglés y español nativos — comunicándonos con fluidez en ambos mundos." },
            why8: { title: "Experiencias a la Medida", body: "Diseñadas en torno a sus intereses, nunca itinerarios prefabricados." },

            // CTA Final
            outroEyebrow: "Planifique Su Estancia",
            outroTitle: "Cada estancia, perfectamente planeada.",
            outroBody: "Ya sea para un fin de semana, una temporada o una estancia prolongada, Pacific Key Management puede coordinar cada detalle antes de su llegada.",
            outroCta: "Hable con Nuestro Equipo Concierge",
        },
        contact: {
            heroEyebrow: "Contacto",
            heroTitle1: "Inicie una",
            heroTitle2: "conversación privada.",
            heroSubtitle: "Un miembro de nuestro equipo se pondrá en contacto en un día hábil — por teléfono, correo o video, en su horario.",
            directEyebrow: "Contacto Directo",
            directTitle: "Nuestra oficina, a su servicio.",
            telephone: "Teléfono",
            emailGeneral: "Consultas Generales",
            emailManagement: "Administración de Propiedades",
            followUs: "Siga a Pacific Key Management",
            office: "Oficina",
            languages: "Idiomas",
            languagesValue: "Inglés · Español",
            serviceAreas: "Áreas de Servicio",
            formEyebrow: "Formulario de Consulta",
            formTitle: "Cuéntenos sobre su residencia.",
        },
        maintConstruct: {
            heroEyebrow: "Mantenimiento y Construcción",
            heroTitle1: "Supervisión de Mantenimiento,",
            heroTitle2: "Renovaciones y Construcción.",
            heroSubtitle: "Protegiendo su inversión mediante mantenimiento proactivo, coordinación de proveedores de confianza y supervisión profesional de proyectos.",
            ctaConsult: "Solicitar Consulta",
            introEyebrow: "Representación del Propietario",
            introTitle: "Sus ojos y oídos en sitio.",
            introP1: "Muchos propietarios pasan solo parte del año en Los Cabos. Ya sea mantenimiento de rutina, una renovación o un proyecto de construcción mayor, Pacific Key Management ofrece supervisión local para garantizar que cada detalle se maneje con profesionalismo y transparencia.",
            introP2: "No actuamos como contratistas. Actuamos como representantes del propietario — coordinando proveedores, verificando la calidad, controlando presupuestos y reportando con documentación fotográfica detallada.",
            maintEyebrow: "Mantenimiento",
            maintTitle: "Cuidado de rutina, ejecutado en silencio.",
            m1: { title: "Programas de Mantenimiento Preventivo", body: "Inspecciones programadas y servicio proactivo — diseñados para detectar problemas antes de que crezcan." },
            m2: { title: "Monitoreo de Equipo Mecánico", body: "Generadores, bombas, sistemas de agua y automatización — revisados y registrados con cadencia regular." },
            m3: { title: "Coordinación de HVAC", body: "Limpieza, cambio de filtros, revisión de refrigerante y coordinación de proveedores para sistemas completos." },
            m4: { title: "Monitoreo de Equipo de Alberca", body: "Bomba, calentador, celda salina y química — coordinados con especialistas verificados." },
            m5: { title: "Coordinación Eléctrica y de Plomería", body: "Oficios locales con licencia, supervisados en sitio — nunca operan sin supervisión." },
            m6: { title: "Preparación Estacional de la Propiedad", body: "Aperturas de temporada, cierres y transiciones de temporadas intermedias, manejadas de principio a fin." },
            m7: { title: "Preparación para Huracanes", body: "Auditorías previas a temporada, instalación de contraventanas, aseguramiento exterior e inspecciones post-tormenta." },
            m8: { title: "Reparaciones de Emergencia", body: "Respuesta 24 / 7 para eventos de agua, energía, seguridad o clima." },
            renoEyebrow: "Renovación",
            renoTitle: "Gestión de proyectos de renovación.",
            renoBody: "Representamos sus intereses desde el concepto hasta el recorrido final — coordinando diseñadores, contratistas y artesanos, y verificando la calidad para que el trabajo terminado coincida con lo prometido en papel.",
            r1: { title: "Renovaciones Interiores", body: "Renovaciones interiores totales o parciales, supervisadas de principio a fin." },
            r2: { title: "Renovación de Cocinas", body: "Carpintería, electrodomésticos y superficies — coordinados con proveedores de lujo." },
            r3: { title: "Remodelación de Baños", body: "Plomería, piedra, accesorios y acabados supervisados para garantizar calidad." },
            r4: { title: "Reemplazo de Pisos", body: "Piedra, madera, azulejo — adquiridos, instalados e inspeccionados." },
            r5: { title: "Programas de Pintura", body: "Interior y exterior — alcance del proveedor, paletas de color y control de calidad." },
            r6: { title: "Mejoras de Vida al Aire Libre", body: "Terrazas, palapas, cocinas y mejoras de jardín." },
            r7: { title: "Instalación de Mobiliario", body: "Recepción, manejo de guante blanco, colocación y montaje." },
            r8: { title: "Renovación de Hogar de Lujo", body: "Refrescamientos curados a mitad de década que protegen el valor de reventa." },
            constructEyebrow: "Construcción",
            constructTitle: "Supervisión de construcción.",
            constructBody: "Para construcciones desde cero y proyectos de capital mayores, nuestro equipo opera como su project manager local — independiente del contratista, enfocado exclusivamente en sus intereses.",
            c1: { title: "Coordinación con Contratistas", body: "Alcances claros, RFIs y juntas semanales con todos los oficios." },
            c2: { title: "Visitas a Sitio", body: "Inspecciones programadas y sorpresa con registros fotográficos." },
            c3: { title: "Monitoreo de Avance", body: "Seguimiento de cronograma, verificación de hitos y alertas de desviación." },
            c4: { title: "Aseguramiento de Calidad", body: "Revisiones independientes de calidad en cada fase de la construcción." },
            c5: { title: "Seguimiento de Presupuesto", body: "Revisión de pagos, control de órdenes de cambio, reportes de contingencias." },
            c6: { title: "Comunicación con Proveedores", body: "Un único punto de contacto bilingüe entre usted y cada oficio." },
            c7: { title: "Reportes para el Propietario", body: "Reportes semanales con fotografías, resúmenes financieros y bitácoras de decisiones." },
            c8: { title: "Documentación del Proyecto", body: "Planos, permisos, garantías y carpetas de cierre organizados para usted." },
            processEyebrow: "Proceso",
            processTitle: "Una cadencia estructurada del proyecto.",
            ph1: { title: "Planeación del Proyecto", body: "Alineación de alcance, marco presupuestal y selección del modelo de entrega correcto." },
            ph2: { title: "Selección de Proveedores", body: "Licitación curada, verificación de referencias y revisión de contratos en su nombre." },
            ph3: { title: "Supervisión del Trabajo", body: "Supervisión diaria en sitio, coordinación de proveedores y resolución de problemas." },
            ph4: { title: "Reportes de Avance", body: "Actualizaciones semanales con fotos, seguimiento financiero y revisión de hitos." },
            ph5: { title: "Verificación de Calidad", body: "Recorridos independientes, listas de pendientes y gestión de punch-list." },
            ph6: { title: "Cierre del Proyecto", body: "Inspecciones finales, cierre documental y entrega limpia al propietario." },
            reportEyebrow: "Reportes",
            reportTitle: "Reportes detallados y transparencia.",
            reportBody: "Cada proyecto — grande o pequeño — corre sobre el mismo marco disciplinado de documentación. Siempre sabrá qué está pasando, qué se decidió y qué sigue.",
            rp1: { label: "Reportes Fotográficos", body: "Documentación visual de cada visita en sitio y de cada hito." },
            rp2: { label: "Actualizaciones de Avance", body: "Resúmenes escritos semanales con fotos y próximos pasos." },
            rp3: { label: "Coordinación de Proveedores", body: "Comunicaciones centralizadas, RFIs y seguimiento de aprobaciones." },
            rp4: { label: "Seguimiento de Presupuesto", body: "Seguimiento en vivo contra el presupuesto aprobado, con bitácora de cambios." },
            rp5: { label: "Documentación de Inspecciones", body: "Inspecciones de fin de fase registradas con fotos y hallazgos." },
            whyEyebrow: "Por Qué PKM",
            whyTitle: "Por qué los propietarios confían en PKM.",
            w1: { title: "Supervisión Independiente", body: "No somos el contratista. Nuestra única lealtad es con sus intereses." },
            w2: { title: "Red de Proveedores de Confianza", body: "Una banca verificada de artesanos, ingenieros y oficios especializados en Los Cabos." },
            w3: { title: "Comunicación Detallada", body: "Reportes semanales, bitácoras de fotos y un único punto de contacto bilingüe." },
            w4: { title: "Presencia Local", body: "En sitio todos los días, en las comunidades y proyectos que servimos." },
            w5: { title: "Experiencia en Propiedades de Lujo", body: "Formados en los acabados, materiales y estándares de las residencias de lujo." },
            w6: { title: "Responsabilidad Durante Todo el Proceso", body: "Decisiones documentadas, aprobaciones firmadas, presupuestos transparentes." },
            outroEyebrow: "¿Planeando un proyecto?",
            outroTitle: "¿Planeando una renovación o proyecto de mejora?",
            outroBody: "Permita que Pacific Key Management le brinde supervisión profesional y coordinación mientras usted disfruta de tranquilidad.",
            outroCta: "Agendar Consulta",
        },

        services_data: {
            "property-management": {
                title: "Administración de Propiedades",
                summary: "Supervisión discreta y proactiva de residencias de lujo — desde inspecciones rutinarias hasta supervisión de proveedores, ejecutada con los estándares de una oficina privada de estates.",
                bullets: ["Inspecciones programadas de la propiedad", "Programa de mantenimiento preventivo", "Supervisión de proveedores de confianza", "Reportes detallados con fotografías"],
            },
            "estate-oversight": {
                title: "Supervisión de Estates",
                summary: "Supervisión especializada de grandes estates privados, con protocolos estrictos de acceso, cuidado del inventario y estándares operativos estructurados.",
                bullets: ["Supervisión de residencias amplias", "Monitoreo de inventario", "Coordinación de accesos y llaves", "Reportes confidenciales"],
            },
            "concierge": {
                title: "Servicios Concierge",
                summary: "De chefs privados a días de yate, su llegada se prepara con cuidado a nivel hospitalario — cada detalle manejado antes de que aterrice.",
                bullets: ["Traslados al aeropuerto y llegadas", "Chefs privados y comidas en la villa", "Reservaciones de yate y golf", "Bienestar, spa y experiencias VIP"],
            },
            "financial-administration": {
                title: "Administración Financiera",
                summary: "Supervisión transparente de las finanzas de la propiedad — servicios públicos, coordinación con la HOA, gestión de facturas y reportes mensuales estructurados.",
                bullets: ["Coordinación de servicios y HOA", "Gestión de facturas de proveedores", "Reportes financieros mensuales", "Soporte contable bilingüe"],
            },
            "rental-oversight": {
                title: "Supervisión de Renta de Lujo",
                summary: "Un programa de renta elevado para propietarios — preparación curada de huéspedes, inspecciones impecables y reportes posteriores a la estancia.",
                bullets: ["Preparación para llegadas de huéspedes", "Inspecciones pre y post estancia", "Lencería, amenidades y rotación", "Estados de cuenta y reportes"],
            },
            "hurricane-preparation": {
                title: "Preparación para Huracanes",
                summary: "Protocolos estacionales estructurados para la preparación ante tormentas, protegiendo su inversión con procedimientos exhaustivos antes y después de la tormenta.",
                bullets: ["Auditorías previas a temporada", "Aseguramiento de contraventanas y exteriores", "Inspecciones post-tormenta", "Coordinación de emergencia 24/7"],
            },
            "maintenance-construction": {
                title: "Mantenimiento y Construcción",
                summary: "Protegiendo y mejorando su residencia mediante mantenimiento preventivo, supervisión de remodelaciones, coordinación de proveedores y supervisión de construcción.",
                bullets: ["Programas de mantenimiento preventivo", "Supervisión de remodelaciones", "Coordinación de construcción", "Supervisión de proveedores y reportes"],
            },
        },
        communities_data: {
            "palmilla": { tag: "Legado icónico de Los Cabos", description: "El enclave de lujo original de Los Cabos — estates frente al campo de golf, villas oceánicas y una dirección con historia silenciosa.", propertyTypes: ["Estates frente al mar", "Villas frente al campo de golf", "Residencias privadas"] },
            "villas-del-mar": { tag: "Residencias gated refinadas", description: "Una comunidad cerrada y serena de hogares privados frente al Mar de Cortés — reconocida por su elegancia discreta y servicio impecable.", propertyTypes: ["Villas frente a la playa", "Casitas", "Estates familiares"] },
            "querencia": { tag: "Santuario solo para miembros", description: "Una comunidad privada de club de golf y océano — arquitectura escultórica, vistas panorámicas y un ritmo residencial tranquilo.", propertyTypes: ["Estates a la medida", "Villas de golf", "Residencias en colina"] },
            "chileno-bay": { tag: "Mar y cielo en diálogo", description: "Un moderno club residencial frente al mar que abraza una de las bahías más nadables de Baja — hogares arquitectónicos con vistas cinematográficas.", propertyTypes: ["Villas modernas", "Hogares frente a la bahía", "Residencias en el cielo"] },
            "maravilla": { tag: "Elegancia en el acantilado", description: "Asentada a lo largo del Corredor Turístico con panorámicas oceánicas impresionantes — una comunidad de estates maduros y bellamente construidos.", propertyTypes: ["Villas en acantilado", "Hogares con vista al mar", "Residencias privadas"] },
            "el-dorado": { tag: "Retiro privado de golf", description: "Una comunidad de golf diseñada por Nicklaus con residencias refinadas y baja densidad — privacidad, deporte y brisas oceánicas en perfecto equilibrio.", propertyTypes: ["Estates de golf", "Villas con vista al mar", "Hogares a la medida"] },
        },
        lifestyle_data: {
            villas: "Villas de Lujo",
            chef: "Experiencias con Chef Privado",
            yacht: "Días de Yate",
            golf: "Golf de Campeonato",
            family: "Llegadas Familiares",
            ocean: "Estilo de Vida Oceánico",
        },
        testimonialsPage: {
            eyebrow: "Pacific Key Management",
            heading: "Testimonios de Clientes",
            quote: "La mayor medida de nuestro éxito es la confianza que nuestros clientes depositan en nosotros.",
            intro: "Conozca la experiencia de propietarios que confían en Pacific Key Management para el cuidado, la comunicación y la supervisión de sus residencias en Los Cabos.",
            filterAll: "Todos los Testimonios",
            loading: "Cargando…",
            emptyTitle: "Historias de nuestros clientes, muy pronto",
            emptyBody: "Nos honra la confianza que depositan en nosotros nuestros propietarios. Las experiencias publicadas de nuestros clientes aparecerán aquí.",
            seoTitle: "Testimonios de Clientes | Pacific Key Management Los Cabos",
            seoDescription: "Conozca la experiencia de propietarios de lujo con Pacific Key Management — el cuidado, la comunicación y la supervisión que ofrecemos en Los Cabos.",
        },
        sharePage: {
            eyebrow: "Pacific Key Management",
            heading: "Comparta Su Experiencia",
            intro: "Gracias por confiar su residencia a Pacific Key Management. Agradeceríamos mucho conocer su experiencia con nuestro equipo.",
            nameLabel: "Nombre",
            emailLabel: "Correo electrónico",
            phoneLabel: "Teléfono",
            communityLabel: "Comunidad",
            serviceLabel: "Servicio",
            experienceLabel: "Su Experiencia",
            selectOptional: "Seleccione (opcional)",
            photoLabel: "Fotografía del cliente (opcional)",
            photoUploading: "Subiendo…",
            photoAttached: "Adjuntada",
            photoHint: "La fotografía es completamente opcional y nunca es obligatoria.",
            displayPrefLabel: "¿Cómo desea que se muestre su nombre si se publica?",
            showCommunityLabel: "¿Podemos mostrar su comunidad?",
            consentLabel: "Autorizo a Pacific Key Management a publicar este testimonio en su sitio web y materiales de marketing utilizando las preferencias de visualización seleccionadas anteriormente.",
            submit: "Compartir Mi Experiencia",
            submitting: "Enviando…",
            consentError: "Por favor autorice la publicación para enviar, o contáctenos directamente.",
            photoTooLarge: "La imagen debe pesar 5 MB o menos.",
            photoAdded: "Fotografía añadida.",
            uploadFailed: "Error al subir la imagen.",
            genericError: "Algo salió mal. Por favor, inténtelo de nuevo.",
            thankyouHeading: "Gracias",
            thankyouBody: "Le agradecemos sinceramente que se haya tomado el tiempo de compartir su experiencia con Pacific Key Management.",
            seoTitle: "Comparta Su Experiencia | Pacific Key Management",
            seoDescription: "Comparta su experiencia con Pacific Key Management.",
            prefFull: "Nombre completo",
            prefFirstLast: "Nombre + inicial del apellido",
            prefFirst: "Solo nombre",
            prefPrivate: "Propietario privado",
            prefContact: "Por favor contáctenme antes de mostrar mi nombre",
        },
    },
};
